/**
 * pm.js — comportamento do tema PetMaison.
 *
 * Um ficheiro, módulos pequenos, tudo orientado a atributos data-pm-*.
 * Regras da casa:
 *  · O JS nunca calcula preços — o drawer re-rende no servidor via
 *    Section Rendering API (/?sections=pm-cart-drawer) e troca o HTML.
 *  · Sem dependências externas.
 *  · localStorage sempre em try/catch (modo privado).
 */
(function () {
  'use strict';

  var storage = {
    get: function (k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem(k, v); } catch (e) { /* modo privado */ } },
    del: function (k) { try { localStorage.removeItem(k); } catch (e) { /* modo privado */ } }
  };

  /* ⚠️ Um IntersectionObserver criado sem ficar guardado numa variável
     pode ser recolhido pelo garbage collector mesmo com alvos a observar
     — foi o que aconteceu à barra de compra do telemóvel: registava-se
     no arranque e, uns segundos depois, deixava de existir; a pessoa
     rolava a ficha toda e a barra nunca aparecia. Tudo o que observa
     passa por aqui e fica com uma referência viva enquanto a página
     viver. */
  var observers = [];
  function keep(o) { observers.push(o); return o; }

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  /* ⚠️ `overflow: hidden` no body NÃO CHEGA — medido a 07-09-2026 com o menu
     aberto: um `scrollTo(0, 600)` levou a página por baixo na mesma, e no iOS o
     dedo faz o mesmo. É a mesma lição que o player de vídeo já tinha aprendido
     (`body.pm-player-open`, 04-09): prende-se o body na posição e repõe-se ao
     largar. Isto vale para o menu, o carrinho, a pesquisa e o popup de uma vez,
     porque todos chamam esta função.
     O contador existe porque dois destes podem sobrepor-se (abrir o carrinho
     com o menu aberto): largar o primeiro não pode desprender a página. */
  var _lockN = 0, _lockY = 0;
  function lockScroll(on) {
    if (on) {
      if (_lockN++ === 0) {
        _lockY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = (-_lockY) + 'px';
        document.body.classList.add('pm-scroll-lock');
      }
      return;
    }
    if (_lockN === 0) return;
    if (--_lockN === 0) {
      document.body.classList.remove('pm-scroll-lock');
      document.body.style.top = '';
      window.scrollTo(0, _lockY);
      /* ⚠️ Quando o menu se fecha pelo botão VOLTAR, o `popstate` corre e o
         browser repõe a SUA posição logo a seguir à nossa — media-se 0 em vez
         dos 300 de onde a pessoa estava. Repetir no quadro seguinte fica por
         cima dele. Medido a 07-09-2026. */
      requestAnimationFrame(function () { window.scrollTo(0, _lockY); });
    }
  }

  /* ── Popup da casa (secção pm-popup) ─────────────────────────────────
     Substitui o Pop Convert. Três regras:
       1. O form funciona SEM este JS — é um {% form 'customer' %} normal,
          com return_to a trazer ?pmpop=ok. Aqui só se evita o recarregar.
       2. Só abre uma vez: fechar OU inscrever-se gravam a data e a memória
          dura os dias que o editor disser. Modo privado não rebenta nada
          (o storage já engole a excepção), no máximo volta a aparecer.
       3. Nunca abre por cima do carrinho, do menu ou da pesquisa — quem
          está a comprar não se interrompe. */
  function initPopup() {
    var root = qs('[data-pm-popup]');
    if (!root) return;

    var chave = root.getAttribute('data-pm-popup-key') || 'pm-popup-v1';
    var dias = parseInt(root.getAttribute('data-pm-popup-dias'), 10) || 30;
    var atraso = parseInt(root.getAttribute('data-pm-popup-atraso'), 10);
    var alvoScroll = parseInt(root.getAttribute('data-pm-popup-scroll'), 10);
    if (isNaN(atraso)) atraso = 12;
    if (isNaN(alvoScroll)) alvoScroll = 35;

    var ask = qs('[data-pm-popup-ask]', root);
    var ok = qs('[data-pm-popup-ok]', root);
    var form = qs('form', root);
    var email = qs('[data-pm-popup-email]', root);
    var aberto = false, armado = true, antes = null;

    function visto() {
      storage.set(chave, String(Date.now()));
    }
    function jaViu() {
      var t = parseInt(storage.get(chave), 10);
      if (!t) return false;
      if (Date.now() - t > dias * 864e5) { storage.del(chave); return false; }
      return true;
    }
    /* Se outro painel está aberto, este espera — não se empilham modais. */
    function ocupado() {
      return (cart && cart.isOpen) || (menu && menu.open) ||
             !!qs('[data-pm-search-overlay]:not(.hidden)');
    }

    function abrir(sucesso) {
      if (aberto) return;
      if (!sucesso && (jaViu() || ocupado())) return;
      aberto = true; armado = false;
      antes = document.activeElement;
      if (sucesso && ask && ok) { ask.hidden = true; ok.hidden = false; }
      root.classList.remove('hidden');
      lockScroll(true);
      var foco = sucesso ? qs('[data-pm-popup-copy]', root) : email;
      if (foco) { try { foco.focus({ preventScroll: true }); } catch (e) { foco.focus(); } }
    }

    function fechar() {
      if (!aberto) return;
      aberto = false;
      root.classList.add('hidden');
      lockScroll(false);
      visto();
      if (antes && antes.focus) { try { antes.focus({ preventScroll: true }); } catch (e) {} }
    }

    qsa('[data-pm-popup-close]', root).forEach(function (b) {
      b.addEventListener('click', fechar);
    });
    document.addEventListener('keydown', function (e) {
      if (aberto && (e.key === 'Escape' || e.key === 'Esc')) fechar();
    });

    /* Prender o tabulador dentro do cartão enquanto estiver aberto. */
    var cartao = qs('[data-pm-popup-card]', root);
    if (cartao) cartao.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !aberto) return;
      var f = qsa('a[href], button:not([disabled]), input:not([type="hidden"])', cartao)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var pri = f[0], ult = f[f.length - 1];
      if (e.shiftKey && document.activeElement === pri) { e.preventDefault(); ult.focus(); }
      else if (!e.shiftKey && document.activeElement === ult) { e.preventDefault(); pri.focus(); }
    });

    /* Copiar o código. Sem clipboard API (http, browser velho) fica a
       seleção do texto, que é o que uma pessoa faria à mão. */
    var copiar = qs('[data-pm-popup-copy]', root);
    if (copiar) copiar.addEventListener('click', function () {
      var cod = copiar.getAttribute('data-pm-popup-code') || '';
      var etiq = qs('[data-pm-popup-copy-label]', copiar);
      function feito() { if (etiq) { var v = etiq.textContent; etiq.textContent = '✓'; setTimeout(function () { etiq.textContent = v; }, 1600); } }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(cod).then(feito, function () {});
      } else {
        var r = document.createRange(); r.selectNodeContents(qs('.pm-pop__code-v', copiar));
        var sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r);
      }
    });

    /* NÃO interceptar o envio. Medido 05-09-2026: um fetch() ao /contact
       responde 400 «Missing CAPTCHA token» — a loja tem o reCAPTCHA
       invisível da Shopify ligado nos formulários de cliente, e o token só
       é injetado quando a Shopify apanha o submit nativo do formulário.
       Qualquer preventDefault() mata o token e a inscrição nunca acontece.
       Por isso o form vai como vai: POST normal, a Shopify trata do
       captcha, o return_to traz de volta a ?pmpop=ok e o popup reabre já
       no código (ver mais abaixo). Uma recarga é o preço de a inscrição
       funcionar mesmo — e é o mesmo caminho que o rodapé já usa.
       Só se marca a memória ANTES de sair, para não voltar a aparecer. */
    if (form) form.addEventListener('submit', function () {
      var btn = qs('[data-pm-popup-submit]', form);
      if (btn) { btn.setAttribute('aria-busy', 'true'); }
      visto();
    });

    /* Voltou do POST: ?pmpop=ok abre já no código; se o Shopify recusou o
       email, a secção marcou data-pm-popup-erro e reabre-se a pedir. */
    /* DOIS marcadores de propósito. O nosso return_to pede ?pmpop=ok, mas
       a Shopify sanitiza o return_to e em vários casos devolve a página com
       o SEU ?customer_posted=true. Não consegui provar qual acontece aqui —
       o captcha invisível recusa browsers automatizados (o form do rodapé,
       que já cá estava, falha na bancada exatamente da mesma maneira) — por
       isso aceitam-se os dois. Custa uma condição e evita o popup ficar mudo
       depois de a pessoa se inscrever. */
    if (/[?&](pmpop=ok|customer_posted=true)\b/.test(location.search)) {
      storage.set(chave, String(Date.now()));
      abrir(true);
      return;
    }
    if (root.hasAttribute('data-pm-popup-erro')) { storage.del(chave); abrir(false); return; }
    if (jaViu()) return;

    if (atraso > 0) setTimeout(function () { if (armado) abrir(false); }, atraso * 1000);
    if (alvoScroll > 0) {
      var aoRolar = function () {
        if (!armado) { window.removeEventListener('scroll', aoRolar); return; }
        var h = document.documentElement.scrollHeight - window.innerHeight;
        if (h > 0 && (window.scrollY / h) * 100 >= alvoScroll) {
          window.removeEventListener('scroll', aoRolar);
          abrir(false);
        }
      };
      window.addEventListener('scroll', aoRolar, { passive: true });
    }
  }

  /* ── Barra de anúncio — fechável, com memória por versão ─────────── */
  function initAnnouncement() {
    var bar = qs('[data-pm-announcement]');
    if (!bar) return;
    var key = bar.getAttribute('data-pm-ann-key');
    if (key && storage.get(key)) { bar.hidden = true; return; }
    var btn = qs('[data-pm-ann-dismiss]', bar);
    if (btn) btn.addEventListener('click', function () {
      bar.hidden = true;
      if (key) storage.set(key, '1');
    });
  }

  /* ── Menu móvel (o catálogo) ─────────────────────────────────────── */
  var menu = {
    el: null, toggle: null, open: false,
    setOpen: function (open) {
      if (!this.el) return;
      // ⚠️ Guarda de idempotência: o `lockScroll` é um CONTADOR, e dois pedidos de
      // abrir para um de fechar deixavam o `body` fixo para sempre — a página nunca
      // mais rolava. O `cart.setOpen` já tinha esta linha; estes três não. Custa
      // nada e tira uma avaria irrecuperável do mapa.
      if (this.open === open) return;
      this.open = open;
      this.el.classList.toggle('hidden', !open);
      this.el.classList.toggle('flex', open);
      this.el.setAttribute('aria-hidden', String(!open));
      if (this.toggle) this.toggle.setAttribute('aria-expanded', String(open));
      lockScroll(open);
      if (open && cart.isOpen) cart.setOpen(false);
      /* ⚠️ O FOCO nunca entrava na gaveta (medido: oito Tab seguidos e todos
         caíram em elementos POR BAIXO dela — quem navega por teclado ou com
         leitor de ecrã percorria a página escondida atrás do menu). Ao abrir, o
         foco vai para o botão de fechar; ao largar, volta ao botão que a abriu.
         O `preventScroll` é preciso: sem ele o browser rola a página presa. */
      if (open) {
        var x = qs('[data-pm-menu-close]', this.el);
        if (x && x.focus) x.focus({ preventScroll: true });
        /* ⚠️ O botão VOLTAR do telemóvel SAÍA DA PÁGINA em vez de fechar o menu
           (medido). Com um estado no histórico, fecha — é o mesmo que o player
           de vídeo já faz. */
        try { history.pushState({ pmMenu: true }, ''); } catch (e) {}
      } else if (this.toggle) {
        this.toggle.focus({ preventScroll: true });
      }
    },
    /** Fechar pela interface volta atrás no histórico, para não deixar o estado
        pendurado; o `popstate` é que fecha mesmo. */
    pedeFecho: function () {
      if (!this.open) return;
      if (history.state && history.state.pmMenu) history.back(); else this.setOpen(false);
    },
    init: function () {
      this.el = qs('[data-pm-menu-drawer]');
      this.toggle = qs('[data-pm-menu-toggle]');
      if (!this.el || !this.toggle) return;
      var self = this;
      this.toggle.addEventListener('click', function () { self.setOpen(!self.open); });
      qsa('[data-pm-menu-close]', this.el).forEach(function (b) {
        b.addEventListener('click', function () { self.pedeFecho(); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.open) self.pedeFecho();
        /* o Tab não sai da gaveta enquanto ela estiver aberta */
        if (e.key !== 'Tab' || !self.open) return;
        var f = qsa('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])', self.el)
          .filter(function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        var pri = f[0], ult = f[f.length - 1];
        if (e.shiftKey && document.activeElement === pri) { e.preventDefault(); ult.focus(); }
        else if (!e.shiftKey && document.activeElement === ult) { e.preventDefault(); pri.focus(); }
      });
      addEventListener('popstate', function () { if (self.open) self.setOpen(false); });
    }
  };

  /* ── Nav desktop — encolher padding/fonte até caber numa linha ───── */
  function initNavFit() {
    var row = qs('[data-pm-nav-row]');
    if (!row || typeof ResizeObserver === 'undefined') return;
    var TIERS = [[0.625, 13], [0.5, 13], [0.4375, 13], [0.375, 12.5], [0.3125, 12], [0.25, 12]];
    function fit() {
      for (var i = 0; i < TIERS.length; i++) {
        row.style.setProperty('--nip', TIERS[i][0] + 'rem');
        row.style.setProperty('--nfs', TIERS[i][1] + 'px');
        if (row.scrollWidth <= row.clientWidth + 1) return;
      }
    }
    fit();
    new ResizeObserver(fit).observe(row);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit).catch(function () {});
  }

  /* ── Drawer do carrinho ──────────────────────────────────────────── */
  var cart = {
    isOpen: false, justAdded: false, bannerTimer: null,
    root: function () { return qs('[data-pm-cart-root]'); },
    panel: function () { return qs('[data-pm-cart-drawer]'); },
    /** O painel branco que desliza — o nó que TEM de sobreviver a um
        re-render, senão a animação de entrada volta a correr. */
    sheet: function () { return qs('[data-pm-cart-panel]'); },
    setOpen: function (open) {
      var p = this.panel();
      if (!p) return;
      if (this.isOpen === open) return;   /* já está assim: não repetir nada */
      this.isOpen = open;
      p.classList.toggle('hidden', !open);
      lockScroll(open);
      if (open && menu.open) menu.setOpen(false);
      if (open) {
        if (this.justAdded) this.showAdded();
        this.loadCrossSell();
      }
    },
    /** Faixa «Adicionado ao carrinho». Um só temporizador: adicionar duas
        vezes seguidas volta a contar do zero em vez de a esconder a meio. */
    showAdded: function () {
      var banner = qs('[data-pm-just-added]', this.panel());
      if (!banner) return;
      this.justAdded = false;
      banner.classList.remove('hidden');
      banner.classList.add('flex');
      if (this.bannerTimer) clearTimeout(this.bannerTimer);
      this.bannerTimer = setTimeout(function () {
        banner.classList.add('hidden');
        banner.classList.remove('flex');
      }, 3500);
    },
    bind: function () {
      var self = this;
      var p = this.panel();
      if (!p) return;
      qsa('[data-pm-cart-close]', p).forEach(function (b) {
        b.addEventListener('click', function () { self.setOpen(false); });
      });
      qsa('[data-pm-qty]', p).forEach(function (b) {
        b.addEventListener('click', function () {
          var parts = b.getAttribute('data-pm-qty').split(':');
          b.disabled = true;
          var linha = b.closest('[data-pm-cart-items] > div');
          if (linha) linha.setAttribute('data-pm-busy', '');   /* resposta imediata ao clique */
          self.change(parseInt(parts[0], 10), parseInt(parts[1], 10));
        });
      });
      promo.bind(p);
    },
    /**
     * Re-rende o drawer a partir do Liquid (os preços nunca são do JS).
     *
     * ⚠️ Com o drawer ABERTO troca-se só o conteúdo do painel, nunca o
     * `[data-pm-cart-root]` inteiro: substituir a raiz cria um véu e um
     * painel NOVOS, e as animações `fade-in`/`slide-in-right` disparam do
     * zero — o drawer voltava a deslizar de fora do ecrã a cada artigo
     * adicionado. Guarda-se também o scroll da lista e o cross-sell já
     * carregado, que de outra forma piscavam a cada alteração.
     */
    refresh: function (thenOpen) {
      var self = this;
      var aberto = this.isOpen;
      return Promise.all([
        fetch('/?sections=pm-cart-drawer', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
        fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })
      ])
        .then(function (res) {
          var html = res[0]['pm-cart-drawer'];
          self.updateBadge(res[1].item_count);
          if (!html) return;
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var fresh = qs('[data-pm-cart-root]', tmp);
          var root = self.root();
          if (!fresh || !root) return;

          var sheetVivo = aberto ? self.sheet() : null;
          var sheetNovo = qs('[data-pm-cart-panel]', fresh);

          if (sheetVivo && sheetNovo) {
            var lista = qs('[data-pm-cart-items]', sheetVivo);
            var scroll = lista ? lista.scrollTop : 0;
            var csVelho = qs('[data-pm-crosssell]', sheetVivo);
            var guardado = csVelho && csVelho.getAttribute('data-pm-loaded')
              ? { pid: csVelho.getAttribute('data-pm-crosssell-pid'), html: csVelho.innerHTML }
              : null;

            sheetVivo.innerHTML = sheetNovo.innerHTML;

            var listaNova = qs('[data-pm-cart-items]', sheetVivo);
            if (listaNova && scroll) listaNova.scrollTop = scroll;

            var csNovo = qs('[data-pm-crosssell]', sheetVivo);
            if (csNovo && guardado && csNovo.getAttribute('data-pm-crosssell-pid') === guardado.pid) {
              csNovo.innerHTML = guardado.html;
              csNovo.setAttribute('data-pm-loaded', '1');
              csNovo.classList.remove('hidden');
              self.bindCrossSell(csNovo);
            }
            self.bind();
            if (self.justAdded) self.showAdded();
            self.loadCrossSell();
          } else {
            root.innerHTML = fresh.innerHTML;
            self.bind();
            if (thenOpen) self.setOpen(true);
          }
        })
        .catch(function (e) {
          /* O servidor não respondeu (offline, 429, secção em falta).
             Com o drawer FECHADO depois de um add, ficar sem sinal nenhum
             é o pior caso — a pessoa não sabe se o artigo entrou; leva-se
             ao carrinho, que é a página que responde à pergunta.
             Com o drawer ABERTO ela está a ver o carrinho: não se lhe
             muda a página por baixo dos pés, devolve-se o erro a quem
             chamou, que sabe avisar onde a pessoa está a olhar. */
          self.justAdded = false;
          if (thenOpen && !aberto) { location.href = '/cart'; return; }
          throw e;
        });
    },
    change: function (line, quantity) {
      var self = this;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        /* O change.js já devolve o carrinho: o contador acerta antes de a
           secção chegar, em vez de ficar um pedido atrás. */
        .then(function (c) { if (c && typeof c.item_count === 'number') self.updateBadge(c.item_count); })
        .catch(function () {})
        .then(function () { return self.refresh(); })
        /* Se o re-render falhar, a linha não pode ficar apagada com o
           botão inerte — devolve-se o drawer ao estado em que estava. */
        .catch(function () {
          var p = self.panel();
          if (!p) return;
          qsa('[data-pm-busy]', p).forEach(function (l) { l.removeAttribute('data-pm-busy'); });
          qsa('[data-pm-qty]', p).forEach(function (b) { b.disabled = false; });
        });
    },
    add: function (variantId, quantity) {
      var self = this;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
      }).then(function (r) {
        if (!r.ok) throw new Error('add ' + r.status);
        self.justAdded = true;
        /* PetMaison Partners: com o artigo já dentro, o `pm_ref` (se houver)
           vai para os atributos do carrinho. Não se espera por ele — o
           afiliado nunca atrasa nem trava a compra. */
        try { ref.sync(); } catch (e) { /* nunca */ }
        return self.refresh(true);
      });
    },
    updateBadge: function (count) {
      qsa('[data-pm-cart-badge]').forEach(function (badge) {
        var prev = parseInt(badge.textContent, 10) || 0;
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.classList.toggle('hidden', count === 0);
        if (count > prev) {
          badge.classList.remove('cart-bump');
          void badge.offsetWidth;
          badge.classList.add('cart-bump');
        }
      });
    },
    /** Os botões «adicionar» das sugestões, ligados onde quer que o bloco
        venha de — do fetch novo ou do que se guardou no re-render. */
    bindCrossSell: function (box) {
      qsa('[data-pm-add-variant]', box).forEach(function (b) {
        b.addEventListener('click', function () {
          b.setAttribute('aria-busy', 'true');
          cart.add(b.getAttribute('data-pm-add-variant'), 1).catch(function () {}).finally(function () { b.removeAttribute('aria-busy'); });
        });
      });
    },
    /* «Complete o essencial» — o Shopify RENDE a secção (Liquid), por isso
       os preços vêm do filtro `money` e o JS nunca os calcula. */
    loadCrossSell: function () {
      var box = qs('[data-pm-crosssell]');
      if (!box || box.getAttribute('data-pm-loaded')) return;
      var pid = box.getAttribute('data-pm-crosssell-pid');
      if (!pid) return;
      box.setAttribute('data-pm-loaded', '1');
      fetch('/recommendations/products?section_id=pm-cart-crosssell&product_id=' + pid + '&limit=8&intent=related')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          /* A resposta traz o wrapper da secção; interessa o conteúdo. */
          var inner = tmp.querySelector('.shopify-section') || tmp;
          if (!inner.textContent.trim()) return;
          box.innerHTML = inner.innerHTML;
          box.classList.remove('hidden');
          cart.bindCrossSell(box);
        })
        .catch(function () { /* sem recomendações */ });
    },
    init: function () {
      var self = this;
      this.bind();
      qsa('[data-pm-cart-open]').forEach(function (b) {
        b.addEventListener('click', function () { self.setOpen(true); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) self.setOpen(false);
      });
      /* Formulários de compra (PDP e afins): interceta e abre o drawer.
         Sem JS o formulário submete-se na mesma — leva `return_to` e
         acaba na página do carrinho. */
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[data-pm-atc]');
        if (!form) return;
        e.preventDefault();
        var id = form.querySelector('[name="id"]');
        var qty = form.querySelector('[name="quantity"]');
        if (!id) return;
        var btn = form.querySelector('[type="submit"]');
        if (btn) btn.setAttribute('aria-busy', 'true');
        self.add(id.value, qty ? parseInt(qty.value, 10) : 1)
          .then(function () { self.clearError(form); })
          /* ⚠️ Aqui submetia-se o formulário à bruta. Quando o pedido
             falha (offline, ou a loja a limitar pedidos), isso atira a
             pessoa para uma página de erro crua e faz-lhe perder a ficha
             que estava a ler. Agora fica onde está e é avisada. */
          .catch(function () { self.showError(form); })
          .finally(function () { if (btn) btn.removeAttribute('aria-busy'); });
      });
    },
    /** Aviso discreto por baixo do botão, quando o servidor não responde. */
    showError: function (form) {
      var el = qs('[data-pm-atc-error]', form);
      if (!el) {
        el = document.createElement('p');
        el.setAttribute('data-pm-atc-error', '');
        el.setAttribute('role', 'alert');
        el.className = 'mt-2 text-tiny font-semibold text-error-700';
        el.textContent = 'Não foi possível adicionar agora. Tente de novo.';
        form.appendChild(el);
      }
      el.hidden = false;
    },
    clearError: function (form) {
      var el = qs('[data-pm-atc-error]', form);
      if (el) el.hidden = true;
    }
  };
  window.PMCart = cart;

  /* ── Código promocional ──────────────────────────────────────────
     O desconto é aplicado AO CARRINHO (POST /cart/update.js com
     `discount`), não guardado à espera do checkout: o Shopify devolve
     logo `discount_codes[].applicable` e o `total_discount`, e o
     carrinho volta a ser desenhado pelo Liquid já com a poupança e o
     total certos. Era isto que faltava — o código entrava e nada na
     página mudava, e um código inválido ficava com ar de aceite.
     O estado (que código está posto) vem do Liquid, do carrinho real;
     nada fica guardado no telemóvel a divergir do servidor. */
  var promo = {
    bind: function (panel) {
      var box = qs('[data-pm-promo]', panel);
      if (!box) return;
      var toggle = qs('[data-pm-promo-toggle]', box);
      var form = qs('[data-pm-promo-form]', box);
      var input = qs('[data-pm-promo-input]', box);
      var botao = qs('[data-pm-promo-apply]', box);
      var errEl = qs('[data-pm-promo-error]', box);
      var t = window.PM_I18N || {};
      var noDrawer = !!box.closest('[data-pm-cart-root]');
      function erro(msg) { errEl.textContent = msg; errEl.classList.remove('hidden'); }
      function show(el, on, flex) {
        el.classList.toggle('hidden', !on);
        if (flex) el.classList.toggle('flex', on);
      }
      /* No drawer o Liquid volta pela Section Rendering API. Na página do
         carrinho o resumo inteiro (subtotal, poupança, total, barra fixa)
         só fica coerente com um reload — é o que já acontece ao mudar
         quantidades, que são submissões do formulário. */
      function redesenha() {
        if (noDrawer && window.PMCart) return window.PMCart.refresh();
        window.location.reload();
        return new Promise(function () {});
      }
      function envia(code) {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code })
        }).then(function (r) { return r.json(); });
      }
      function aplica() {
        var code = (input.value || '').trim().toUpperCase();
        if (!code) { erro(t.promo_vazio || 'Introduza um código'); return; }
        botao.disabled = true;
        errEl.classList.add('hidden');
        envia(code).then(function (carrinho) {
          var ok = (carrinho.discount_codes || []).some(function (d) {
            return (d.code || '').toUpperCase() === code && d.applicable;
          });
          if (!ok) {
            /* Um código recusado fica na lista do carrinho com
               `applicable: false`; tira-se para não ir sujar o checkout. */
            return envia('').then(function () {
              botao.disabled = false;
              erro(t.promo_invalido || 'Código inválido ou expirado');
            });
          }
          /* Nada de cookie `/discount/`: medido a 31-08-2026 — o desconto
             posto no CARRINHO já chega ao checkout sozinho (399,00 →
             339,15 com PET15). O cookie só servia para ressuscitar um
             código que a pessoa tinha acabado de remover. */
          input.value = '';
          return redesenha();
        }).catch(function () {
          botao.disabled = false;
          erro(t.promo_erro || 'Não foi possível aplicar o código. Tente outra vez.');
        });
      }
      toggle.addEventListener('click', function () {
        show(toggle, false);
        show(form, true);
        /* No telemóvel NÃO se rouba o foco: o teclado abre por cima do
           drawer, empurra o painel e o campo fica meio escondido — era
           isto que parecia avariado. No computador o foco ajuda. */
        if (window.innerWidth > 699) input.focus();
      });
      qs('[data-pm-promo-cancel]', box).addEventListener('click', function () {
        show(form, false); show(toggle, true); errEl.classList.add('hidden');
      });
      qs('[data-pm-promo-remove]', box).addEventListener('click', function (e) {
        e.currentTarget.disabled = true;
        envia('').then(redesenha).catch(function () {
          erro(t.promo_erro || 'Não foi possível remover o código.');
        });
      });
      botao.addEventListener('click', aplica);
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); aplica(); } });
    }
  };

  /* ── Overlay de pesquisa — sugestões da pesquisa preditiva ───────── */
  var search = {
    el: null, input: null, results: null, timer: null, aberto: false,
    setOpen: function (open) {
      if (!this.el) return;
      // ⚠️ Guarda de idempotência: o `lockScroll` é um CONTADOR, e dois pedidos de
      // abrir para um de fechar deixavam o `body` fixo para sempre — a página nunca
      // mais rolava. O `cart.setOpen` já tinha esta linha; estes três não. Custa
      // nada e tira uma avaria irrecuperável do mapa.
      if (this.aberto === open) return;
      this.aberto = open;
      this.el.classList.toggle('hidden', !open);
      lockScroll(open);
      if (open && this.input) this.input.focus();
    },
    init: function () {
      this.el = qs('[data-pm-search-overlay]');
      if (!this.el) return;
      var self = this;
      this.input = qs('[data-pm-search-input]', this.el);
      this.results = qs('[data-pm-search-results]', this.el);
      qsa('[data-pm-search-open]').forEach(function (b) {
        b.addEventListener('click', function () { self.setOpen(true); });
      });
      qsa('[data-pm-search-close]', this.el).forEach(function (b) {
        b.addEventListener('click', function () { self.setOpen(false); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !self.el.classList.contains('hidden')) self.setOpen(false);
      });
      if (this.input) this.input.addEventListener('input', function () {
        clearTimeout(self.timer);
        self.timer = setTimeout(function () { self.suggest(self.input.value.trim()); }, 250);
      });
    },
    suggest: function (q) {
      var self = this;
      if (!q || q.length < 2) { this.results.innerHTML = ''; return; }
      /* O Shopify RENDE a secção (Liquid) — os preços vêm do filtro
         `money` e o JS não formata nada. Ver pm-search-suggest.liquid. */
      fetch('/search/suggest?section_id=pm-search-suggest&q=' + encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=6')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var inner = tmp.querySelector('.shopify-section') || tmp;
          self.results.innerHTML = inner.innerHTML;
        })
        .catch(function () { /* offline — fica o que está */ });
    }
  };

  /* ── Revelar imagens cv-fade quando carregam (o handler global que o
        site tem no root: sem ele as imagens ficariam a opacity 0). ──── */
  function initImageFade() {
    function mark(img) { img.classList.add('cv-loaded'); }
    function watch(img) {
      if (!img.classList || !img.classList.contains('cv-fade')) return;
      if (img.complete && img.naturalWidth > 0) { mark(img); return; }
      img.addEventListener('load', function () { mark(img); }, { once: true });
      img.addEventListener('error', function () { mark(img); }, { once: true });
    }
    qsa('img.cv-fade').forEach(watch);
    /* Conteúdo que chega depois (drawer re-rendido, cross-sell). */
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          watch(node);
          if (node.querySelectorAll) qsa('img.cv-fade', node).forEach(watch);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── Rails de produtos — setas, pontas e barra de progresso ──────── */
  function initRails() {
    var ON = 'border-ink/20 text-ink/70 bg-white shadow-sm hover:border-ink hover:text-ink hover:shadow-md cursor-pointer'.split(' ');
    var OFF = 'border-ink/10 text-ink/20 cursor-default'.split(' ');
    qsa('[data-pm-rail]').forEach(function (rail) {
      var track = qs('[data-pm-rail-track]', rail);
      var prev = qs('[data-pm-rail-prev]', rail);
      var next = qs('[data-pm-rail-next]', rail);
      var progress = qs('[data-pm-rail-progress]', rail);
      if (!track) return;
      function setState(btn, enabled) {
        if (!btn) return;
        btn.disabled = !enabled;
        ON.forEach(function (c) { btn.classList.toggle(c, enabled); });
        OFF.forEach(function (c) { btn.classList.toggle(c, !enabled); });
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth;
        setState(prev, track.scrollLeft > 4);
        setState(next, track.scrollLeft < max - 4);
        if (progress && max > 0) {
          var frac = Math.min(1, track.clientWidth / track.scrollWidth);
          var pos = Math.min(1 - frac, track.scrollLeft / track.scrollWidth);
          progress.style.width = (frac * 100) + '%';
          progress.style.transform = 'translateX(' + (pos / frac) * 100 + '%)';
        }
      }
      function step(dir) {
        var card = track.firstElementChild;
        if (!card) return;
        var gap = window.innerWidth >= 640 ? 24 : 12;
        var visible = Math.max(1, Math.floor(track.clientWidth / (card.offsetWidth + gap)));
        track.scrollBy({ left: dir * (card.offsetWidth + gap) * visible, behavior: 'smooth' });
        setTimeout(update, 450);
      }
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', update, { passive: true });
      update();
      /* Aquecer as imagens lazy quando o rail se aproxima do viewport. */
      if (typeof IntersectionObserver !== 'undefined') {
        var io = keep(new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            qsa('img[loading="lazy"]', track).forEach(function (img) { img.loading = 'eager'; });
            io.disconnect();
          });
        }, { rootMargin: '200px 0px' }));
        io.observe(track);
      }
    });
  }

  /* ── PDP: galeria (telemóvel swipe + pontos + lightbox · desktop
        miniaturas + zoom ao clique) ───────────────────────────────── */
  function initGallery() {
    var pdp = qs('[data-pm-pdp]');
    if (!pdp) return;
    var dataEl = qs('[data-pm-gal-data]');
    var urls = [];
    try { urls = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (e) {}
    var current = 0;

    /* Telemóvel: swipe + pontos + contador */
    var track = qs('[data-pm-gal-track]', pdp);
    var dots = qsa('[data-pm-gal-dot]', pdp);
    var counter = qs('[data-pm-gal-counter]', pdp);
    function setDot(idx) {
      dots.forEach(function (d, i) {
        var span = d.firstElementChild;
        if (!span) return;
        span.className = 'block rounded-2xl transition-all duration-base ' + (i === idx ? 'w-5 h-[3px] bg-ink' : 'w-[5px] h-[5px] bg-ink/20');
      });
      if (counter) counter.textContent = String(idx + 1);
    }
    if (track) {
      track.addEventListener('scroll', function () {
        if (track.__raf) return;
        track.__raf = true;
        requestAnimationFrame(function () {
          track.__raf = false;
          var idx = Math.round(track.scrollLeft / track.clientWidth);
          if (idx !== current && idx >= 0 && idx < urls.length) { current = idx; setDot(idx); }
        });
      }, { passive: true });
      dots.forEach(function (d) {
        d.addEventListener('click', function () {
          var i = parseInt(d.getAttribute('data-pm-gal-dot'), 10);
          track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
        });
      });
    }

    /* Lightbox (telemóvel) */
    var lb = qs('[data-pm-gal-lightbox]', pdp);
    var lbTrack = qs('[data-pm-gal-lb-track]', pdp);
    var lbCounter = qs('[data-pm-gal-lb-counter]', pdp);
    function openLb(idx) {
      if (!lb) return;
      lb.classList.remove('hidden');
      lb.classList.add('flex');
      lockScroll(true);
      requestAnimationFrame(function () { if (lbTrack) lbTrack.scrollLeft = idx * lbTrack.clientWidth; });
    }
    function closeLb() {
      if (!lb) return;
      lb.classList.add('hidden');
      lb.classList.remove('flex');
      lockScroll(false);
    }
    qsa('[data-pm-gal-open]', pdp).forEach(function (img) {
      img.addEventListener('click', function () { openLb(parseInt(img.getAttribute('data-pm-gal-open'), 10)); });
    });
    qsa('[data-pm-gal-close]', pdp).forEach(function (b) { b.addEventListener('click', closeLb); });
    if (lbTrack) {
      lbTrack.addEventListener('scroll', function () {
        var idx = Math.round(lbTrack.scrollLeft / lbTrack.clientWidth);
        if (lbCounter) lbCounter.textContent = String(idx + 1);
      }, { passive: true });
      lbTrack.addEventListener('click', closeLb);
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lb && !lb.classList.contains('hidden')) closeLb();
    });

    /* Desktop: miniaturas trocam a principal; clique = zoom 1.6× no ponto */
    var mainImg = qs('[data-pm-gal-main-img]', pdp);
    var mainFig = qs('[data-pm-gal-main]', pdp);
    var thumbs = qsa('[data-pm-gal-thumb]', pdp);
    var zoomed = false;
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        var i = parseInt(t.getAttribute('data-pm-gal-thumb'), 10);
        if (mainImg && urls[i]) { mainImg.src = urls[i]; mainImg.removeAttribute('srcset'); }
        thumbs.forEach(function (o, j) {
          var sel = j === i;
          o.classList.toggle('opacity-50', !sel);
          o.classList.toggle('hover:opacity-90', !sel);
          var ring = o.querySelector('[data-pm-gal-thumb-ring]');
          if (!ring) return;
          ring.classList.toggle('ring-2', sel);
          ring.classList.toggle('ring-ink', sel);
          ring.classList.toggle('ring-ink/10', !sel);
        });
      });
    });
    function setZoom(on, e) {
      if (!mainImg || !mainFig) return;
      zoomed = on;
      if (on && e) {
        var r = mainFig.getBoundingClientRect();
        mainImg.style.transformOrigin = (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '% ' + (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%';
      } else {
        mainImg.style.transformOrigin = '50% 50%';
      }
      mainImg.style.transform = on ? 'scale(1.6)' : 'scale(1)';
      mainFig.classList.toggle('cursor-zoom-in', !on);
      mainFig.classList.toggle('cursor-zoom-out', on);
      var hint = qs('[data-pm-gal-zoom-hint]', pdp);
      if (hint) hint.style.display = on ? 'none' : '';
    }
    if (mainFig) {
      mainFig.addEventListener('click', function (e) { setZoom(!zoomed, e); });
      mainFig.addEventListener('mousemove', function (e) {
        if (!zoomed || !mainImg) return;
        var r = mainFig.getBoundingClientRect();
        mainImg.style.transformOrigin = (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '% ' + (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%';
      });
      mainFig.addEventListener('mouseleave', function () { setZoom(false); });
    }
  }

  /* ── PDP: descrição «Ver mais», variantes, barra fixa, entrega ───── */
  function initPdp() {
    var pdpForm = qs('form[data-pm-atc]');

    /* Descrição curta (telemóvel) */
    var desc = qs('[data-pm-desc]');
    var toggle = qs('[data-pm-desc-toggle]');
    if (desc && toggle) toggle.addEventListener('click', function () {
      var open = desc.classList.toggle('line-clamp-3') === false;
      toggle.textContent = open ? toggle.getAttribute('data-less') : toggle.getAttribute('data-more');
      toggle.setAttribute('aria-expanded', String(open));
    });
    /* O «Ver mais» só existe quando há mais texto do que o corte (3 linhas no
       telemóvel, 4 no computador): uma descrição de duas frases não tem botão. */
    function syncDescToggle() {
      if (!desc || !toggle) return;
      if (desc.classList.contains('line-clamp-3')) toggle.hidden = desc.scrollHeight <= desc.clientHeight + 2;
    }
    syncDescToggle();
    window.addEventListener('resize', syncDescToggle);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncDescToggle);

    /* Variantes (quando existirem) */
    qsa('[data-pm-variant]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled) return;
        var input = pdpForm && pdpForm.querySelector('[name="id"]');
        if (input) input.value = b.getAttribute('data-pm-variant');
        qsa('[data-pm-variant]').forEach(function (o) {
          var sel = o === b;
          o.setAttribute('aria-checked', String(sel));
          o.className = o.className.replace(/bg-ink text-white border-ink|bg-white text-ink border-ink\/12 hover:border-ink/g, sel ? 'bg-ink text-white border-ink' : 'bg-white text-ink border-ink/12 hover:border-ink');
        });
        var price = b.getAttribute('data-price');
        ['[data-pm-price]', '[data-pm-atc-price]', '[data-pm-sticky-price]'].forEach(function (sel2) {
          var el = qs(sel2);
          if (el && price) el.textContent = price;
        });
        prest(b.getAttribute('data-cents'));
      });
    });

    /* Packs na caixa de compra («Escolha a sua configuração», 04-09-2026):
       escolher um pack troca o `id` que o formulário envia — o botão passa a
       adicionar o pack — e o preço grande, o do botão e o da barra fixa; o
       riscado e o «Poupe» seguem a opção (soma em separado menos o pack). */
    /* A prestacao acompanha a variante/pack escolhido. A conta e a MESMA do
       `pm-prestacoes` (cêntimos a dividir por 3, TRUNCADO — e assim que a Klarna
       arredonda: 3500 dá 11,66 e não 11,67) e o formato sai do que o servidor
       ja escreveu, para nao haver duas maneiras de escrever um preco. */
    function prest(cents) {
      var el = qs('[data-pm-prest-valor]');
      if (!el || !cents) return;
      var antes = el.textContent, dec = antes.indexOf(',') > -1 ? ',' : '.';
      var v = Math.floor(parseInt(cents, 10) / 3) / 100;
      var txt = v.toFixed(2).replace('.', dec);
      el.textContent = antes.replace(/[0-9]+[.,][0-9]{2}/, txt);
    }

    qsa('[data-pm-pack]').forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        var input = pdpForm && pdpForm.querySelector('[name="id"]');
        if (input) input.value = r.value;
        var price = r.getAttribute('data-price');
        ['[data-pm-price]', '[data-pm-atc-price]', '[data-pm-sticky-price]'].forEach(function (s) {
          var el = qs(s); if (el && price) el.textContent = price;
        });
        prest(r.getAttribute('data-cents'));
        var cmp = qs('[data-pm-compare]'), sav = qs('[data-pm-saving]');
        var c = r.getAttribute('data-compare') || '', sv = r.getAttribute('data-saving') || '';
        if (cmp) { cmp.textContent = c; cmp.hidden = !c; }
        if (sav) { sav.textContent = sv; sav.hidden = !sv; }
        var nm = qs('[data-pm-sticky-name]'), n = r.getAttribute('data-name');
        if (nm && n) nm.textContent = n;
        qsa('[data-pm-pack]').forEach(function (o) {
          var row = o.closest('.pm-pick'); if (row) row.classList.toggle('is-on', o.checked);
        });
      });
    });

    /* Barra ATC fixa — aparece quando o CTA sai por CIMA do ecrã.

       ⚠️ O observador sozinho NÃO chega, e isto custou a apanhar (31-08):
       um IntersectionObserver só chama quando o ESTADO cruza o limiar. Num
       salto de scroll — âncora do URL, tecla End, o browser a repor a
       posição ao voltar atrás — o botão entra e sai do ecrã dentro do
       mesmo frame; o estado antes e depois é o mesmo («fora»), não há
       cruzamento nenhum e o observador cala-se. Resultado: a ficha rolada
       até ao fim com o botão a 4000px acima e a barra escondida.

       Por isso a decisão passa a estar numa função só, chamada pelas duas
       vias: pelo observador (o caminho barato, quando o utilizador rola a
       sério) e por um listener de scroll passivo limitado a um cálculo por
       frame (a correção, para os saltos). Uma leitura de `rect` por frame
       num elemento é barata; perder a barra de compra não é. */
    var sticky = qs('[data-pm-sticky-atc]');
    var inline = document.getElementById('pdp-atc-inline');
    if (sticky && inline) {
      var atcVisivel = null;
      var atcAgendado = false;
      var atualizarAtc = function () {
        atcAgendado = false;
        var show = inline.getBoundingClientRect().top < 0;
        if (show === atcVisivel) return;          /* não mexe no DOM à toa */
        atcVisivel = show;
        sticky.classList.toggle('translate-y-full', !show);
        sticky.classList.toggle('translate-y-0', show);
        sticky.classList.toggle('pointer-events-none', !show);
        sticky.setAttribute('aria-hidden', String(!show));
      };
      var pedirAtc = function () {
        if (atcAgendado) return;
        atcAgendado = true;
        requestAnimationFrame(atualizarAtc);
      };
      if ('IntersectionObserver' in window) {
        keep(new IntersectionObserver(pedirAtc)).observe(inline);
      }
      window.addEventListener('scroll', pedirAtc, { passive: true });
      window.addEventListener('resize', pedirAtc, { passive: true });
      atualizarAtc();
    }

    /* Botões de compra fora do formulário (barra fixa, fecho) */
    qsa('[data-pm-atc-remote]').forEach(function (b) {
      b.addEventListener('click', function () {
        var input = pdpForm && pdpForm.querySelector('[name="id"]');
        if (!input) return;
        b.setAttribute('aria-busy', 'true');
        cart.add(input.value, 1)
          .catch(function () { if (pdpForm) cart.showError(pdpForm); })
          .finally(function () { b.removeAttribute('aria-busy'); });
      });
    });

    /* «Juntar» dos acompanhamentos e afins */
    qsa('[data-pm-add-variant]').forEach(function (b) {
      b.addEventListener('click', function () {
        b.setAttribute('aria-busy', 'true');
        cart.add(b.getAttribute('data-pm-add-variant'), 1)
          .catch(function () { if (pdpForm) cart.showError(pdpForm); })
          .finally(function () { b.removeAttribute('aria-busy'); });
      });
    });

    /* Promessa de entrega — a regra da loja: antes das 13:00 de Lisboa
       contam-se 2 dias úteis, depois 3; fins de semana não contam. */
    /* ⚠️ Só em Portugal. O bloco conta 2 ou 3 dias úteis a partir das 13:00 de
       Lisboa e escreve «receba até <dia>» — é a promessa de Portugal Continental.
       Fora daqui a política diz 3 a 7 dias úteis, e o Liquid já lá deixou essa
       janela sem data; `PM_I18N.pt` vem do país da loja, não da língua. Sem esta
       porta, a ficha francesa prometia uma data portuguesa (05-09-2026). */
    var dEl = (window.PM_I18N || {}).pt ? qs('[data-pm-delivery]') : null;
    if (dEl) {
      try {
        var parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Lisbon', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit' }).formatToParts(new Date());
        function g(t) { var p = parts.find(function (x) { return x.type === t; }); return p ? Number(p.value) : 0; }
        var hour = g('hour') % 24;
        var before = hour < 13;
        var d = new Date(Date.UTC(g('year'), g('month') - 1, g('day'), 12));
        var need = before ? 2 : 3, added = 0;
        while (added < need) {
          d.setUTCDate(d.getUTCDate() + 1);
          var wd = d.getUTCDay();
          if (wd >= 1 && wd <= 5) added++;
        }
        /* A data escreve-se na língua da loja: o `lang` do documento é
           `pt-PT`, `fr`, `es` ou `en` conforme o domínio, e o Intl trata
           do resto (dia da semana e mês). Antes eram dois arrays em
           português e a promessa de entrega saía «quinta-feira, 3 de
           setembro» também em França. */
        var lang = document.documentElement.lang || 'pt-PT';
        var quando = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(d);
        var i18n = window.PM_I18N || {};
        /* ⚠️ Num PACK a linha já começa com o nome do artigo («PetMaison Feeder ·»),
           e com o «Entrega grátis ·» à frente da data ficavam DOIS separadores e
           601 px de texto numa coluna de 501 — partia em duas linhas (medido a
           1440). Com o texto curto são 476 px e cabe numa. O elemento pede-o com
           `data-pm-delivery-curto`. */
        var curto = dEl.hasAttribute('data-pm-delivery-curto');
        /* 27-10-2026 (#283): abaixo de 45 € há portes de 4,90 € — a ficha pede o texto sem «grátis»
           com `data-pm-delivery-pago`. */
        var pago = dEl.hasAttribute('data-pm-delivery-pago');
        var lead = curto ? (i18n.entrega_curto || 'receba até ')
                 : before ? (i18n.entrega_antes || 'Encomende até às 13:00 — receba até ')
                 : pago ? (i18n.entrega_pago || 'Receba até ')
                          : (i18n.entrega_depois || 'Entrega grátis — receba até ');
        /* ⚠️ SEM `whitespace-nowrap` (07-09-2026, dono: «tenta que fique até ao fim
           e corte o resto para baixo se necessário, mas não forces sempre em 2
           linhas»). A data inteira era indivisível: media 197 px e sobravam ~190
           no fim da linha do pack — faltavam SETE e ela saltava toda para baixo,
           deixando meia linha vazia. Agora quebra onde tem de quebrar e a linha
           enche-se até ao fim.
           O que continua junto é o DIA e o MÊS: do primeiro algarismo em diante os
           espaços passam a duros, para não se ler «10 de» numa linha e «setembro»
           na outra. Vale em qualquer língua da loja (pt/fr/es/en/de/it/nl/pl), que
           é onde o Intl põe o número antes do mês. */
        var junto = quando.replace(/\d[\s\S]*$/, function (fim) { return fim.replace(/ /g, '\u00A0'); });
        dEl.innerHTML = lead + '<strong class="font-bold text-ink">' + junto + '</strong>';
      } catch (e) { /* fica o texto do servidor */ }
    }
  }

  /* ── Coleção: ordenação, drawer de filtros ───────────────────────── */
  function initCollection() {
    /* Ordenação — dropdown próprio; escrever `sort_by` no URL e recarregar.
       (`manual` é o default e sai do URL, para o «copiar link» ficar limpo.) */
    var sortBox = qs('[data-pm-sort]');
    if (sortBox) {
      var toggle = qs('[data-pm-sort-toggle]', sortBox);
      var menu = qs('[data-pm-sort-menu]', sortBox);
      var chev = toggle && toggle.querySelector('svg:last-of-type');
      function closeMenu() {
        menu.classList.add('hidden');
        toggle.setAttribute('aria-expanded', 'false');
        if (chev) chev.classList.remove('rotate-180');
      }
      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = menu.classList.toggle('hidden') === false;
        toggle.setAttribute('aria-expanded', String(open));
        if (chev) chev.classList.toggle('rotate-180', open);
      });
      document.addEventListener('click', function () { if (!menu.classList.contains('hidden')) closeMenu(); });
      menu.addEventListener('click', function (e) { e.stopPropagation(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !menu.classList.contains('hidden')) closeMenu(); });
      qsa('[data-pm-sort-opt]', menu).forEach(function (opt) {
        opt.addEventListener('click', function () {
          var val = opt.getAttribute('data-pm-sort-opt');
          var u = new URL(location.href);
          if (val === 'manual') u.searchParams.delete('sort_by');
          else u.searchParams.set('sort_by', val);
          u.searchParams.delete('page');
          location.href = u.toString();
        });
      });
    }

    /* Drawer de filtros — compõe-se a seleção toda e só depois aplica
       («stage and apply»), como no site: um reload por combinação, não
       um por clique. */
    var drawer = qs('[data-pm-filters-drawer]');
    if (!drawer) return;
    var drawerAberto = false;
    function setDrawer(open) {
      // ⚠️ Guarda de idempotência: o `lockScroll` é um CONTADOR, e dois pedidos de
      // abrir para um de fechar deixavam o `body` fixo para sempre — a página nunca
      // mais rolava. O `cart.setOpen` já tinha esta linha; estes três não. Custa
      // nada e tira uma avaria irrecuperável do mapa.
      if (drawerAberto === open) return;
      drawerAberto = open;
      drawer.classList.toggle('hidden', !open);
      lockScroll(open);
      qsa('[data-pm-filters-open]').forEach(function (b) { b.setAttribute('aria-expanded', String(open)); });
    }
    qsa('[data-pm-filters-open]').forEach(function (b) { b.addEventListener('click', function () { setDrawer(true); }); });
    qsa('[data-pm-filters-close]', drawer).forEach(function (b) { b.addEventListener('click', function () { setDrawer(false); }); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !drawer.classList.contains('hidden')) setDrawer(false);
    });
    var apply = qs('[data-pm-filters-apply]', drawer);
    var form = qs('[data-pm-filters-form]', drawer);
    if (apply && form) apply.addEventListener('click', function () {
      var params = new URLSearchParams(new FormData(form));
      location.href = location.pathname + (params.toString() ? '?' + params.toString() : '');
    });

    /* FAB esconde-se ao descer, volta ao subir. */
    var fab = qs('[data-pm-filters-fab]');
    if (fab) {
      var lastY = window.scrollY;
      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        var down = y > lastY && y > 200;
        fab.classList.toggle('translate-y-[150%]', down);
        fab.classList.toggle('opacity-0', down);
        fab.classList.toggle('pointer-events-none', down);
        lastY = y;
      }, { passive: true });
    }
  }

  /* ── Entrar: alternar entre login e «esqueci-me» ─────────────────── */
  function initLogin() {
    var login = document.getElementById('login');
    var recover = document.getElementById('recover');
    if (!login || !recover) return;
    function show(which) {
      login.classList.toggle('hidden', which === 'recover');
      recover.classList.toggle('hidden', which !== 'recover');
    }
    qsa('[data-pm-recover-open]').forEach(function (b) { b.addEventListener('click', function () { show('recover'); }); });
    qsa('[data-pm-recover-cancel]').forEach(function (b) { b.addEventListener('click', function () { show('login'); }); });
    /* O Shopify volta com #recover no URL depois de submeter. */
    if (location.hash === '#recover' || recover.querySelector('.border-pitch\\/30')) show('recover');
  }

  /* ── Página de carrinho ──────────────────────────────────────────── */
  function initCartPage() {
    qsa('[data-pm-cart-qty]').forEach(function (b) {
      b.addEventListener('click', function () {
        var parts = b.getAttribute('data-pm-cart-qty').split(':');
        b.disabled = true;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(parts[0], 10), quantity: parseInt(parts[1], 10) })
        }).then(function () { location.reload(); });
      });
    });
    /* O widget de promo da página usa o mesmo módulo do drawer. */
    var page = qs('[data-pm-promo]');
    if (page && !page.closest('[data-pm-cart-root]')) promo.bind(document);
  }

  /* ── Tabs geradas, FAQ «ver mais», vídeos e lightbox ─────────────── */
  function initContent() {
    qsa('[data-pm-tabs]').forEach(function (box) {
      var tabs = qsa('[data-pm-tab]', box);
      var panels = qsa('[data-pm-tab-panel]', box);
      tabs.forEach(function (t) {
        t.addEventListener('click', function () {
          var i = t.getAttribute('data-pm-tab');
          tabs.forEach(function (o) {
            var sel = o === t;
            o.setAttribute('aria-selected', String(sel));
            o.className = 'shrink-0 whitespace-nowrap px-5 py-3.5 text-sm font-semibold transition-colors ' + (sel ? 'text-ink border-b-2 border-signal-500 bg-white' : 'text-ink/50');
          });
          panels.forEach(function (p) { p.classList.toggle('hidden', p.getAttribute('data-pm-tab-panel') !== i); });
        });
      });
    });

    /* Opiniões da Trustpilot: abrir/fechar as três linhas. */
    qsa('[data-pm-tp-more]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('figure');
        var text = card && card.querySelector('[data-pm-tp-text]');
        if (!text) return;
        var open = text.classList.toggle('line-clamp-3') === false;
        btn.textContent = open ? btn.getAttribute('data-less') : btn.getAttribute('data-more');
        btn.setAttribute('aria-expanded', String(open));
      });
    });

    qsa('[data-pm-faq-more]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.getAttribute('data-less') : btn.getAttribute('data-more');
        qsa('[data-pm-faq-extra]').forEach(function (d) { d.classList.toggle('hidden', !open); });
      });
    });

    /* FAQ por categoria — os «cartões» que o tema anterior tinha. Em
       «Todas» vale a regra de sempre (5 visíveis + «Ver mais»); dentro de
       uma categoria mostram-se todas as perguntas dela e o «Ver mais» sai
       da frente, porque já não há nada escondido para abrir. */
    /* ── Detalhes: chips por artigo, nos packs ────────────────────────
       Dono 05-09-2026: num pack, os separadores de todos os artigos
       empilhados são uma lista sem fim. Escolhe-se o artigo e vê-se só o
       dele, com o primeiro separador aberto. Sem chips (artigo solto)
       este bloco não faz nada. */
    qsa('[data-pm-det-cats]').forEach(function (barra) {
      var sec = barra.closest('section');
      if (!sec) return;
      function aplica(cat) {
        var primeiro = null;
        qsa('[data-pm-det-item]', sec).forEach(function (item) {
          var mostra = item.getAttribute('data-pm-det-cat-item') === cat;
          item.classList.toggle('hidden', !mostra);
          if (!mostra) {
            item.classList.remove('is-open');
            var b = qs('.pm-det__tab', item), p = qs('.pm-det__panel', item);
            if (b) b.setAttribute('aria-expanded', 'false');
            if (p) p.hidden = true;
          } else if (!primeiro) { primeiro = item; }
        });
        /* um aberto, sempre: o primeiro separador do artigo escolhido */
        if (primeiro && !qs('[data-pm-det-item]:not(.hidden).is-open', sec)) {
          primeiro.classList.add('is-open');
          var b1 = qs('.pm-det__tab', primeiro), p1 = qs('.pm-det__panel', primeiro);
          if (b1) b1.setAttribute('aria-expanded', 'true');
          if (p1) p1.hidden = false;
        }
      }
      qsa('[data-pm-det-cat]', barra).forEach(function (chip) {
        chip.addEventListener('click', function () {
          qsa('[data-pm-det-cat]', barra).forEach(function (c) { c.setAttribute('aria-selected', 'false'); });
          chip.setAttribute('aria-selected', 'true');
          aplica(chip.getAttribute('data-pm-det-cat'));
        });
      });
    });

    /* Capítulos de um pack: uma pastilha por artigo, um painel de cada vez.
       Ver `agrupar_capitulos` no gerador — o Ultimate Care empilhava três
       fichas inteiras e dava 16.831 px no telemóvel. */
    qsa('[data-pm-cap-bar]').forEach(function (barra) {
      var chips = qsa('[data-pm-cap]', barra);
      var blocos = qsa('[data-pm-cap-bloco]');
      if (!chips.length || !blocos.length) return;
      function aplica(cat) {
        blocos.forEach(function (bl) { bl.hidden = bl.getAttribute('data-pm-cap-bloco') !== cat; });
        chips.forEach(function (c) { c.setAttribute('aria-selected', c.getAttribute('data-pm-cap') === cat ? 'true' : 'false'); });
        /* ⚠️ Trocar de capítulo pára o que estava a tocar no anterior: um <video>
           dentro de um bloco escondido continua a descarregar (é a mesma lição do
           player, hoje). */
        qsa('video').forEach(function (v) {
          if (v.closest('[data-pm-cap-bloco]') && v.closest('[data-pm-cap-bloco]').hidden && !v.paused) v.pause();
        });
      }
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () { aplica(chip.getAttribute('data-pm-cap')); });
      });
    });

    qsa('[data-pm-faq-cats]').forEach(function (barra) {
      var sec = barra.closest('section');
      if (!sec) return;
      var mais = qs('[data-pm-faq-more]', sec);
      function aplica(cat) {
        var aberto = mais && mais.getAttribute('aria-expanded') === 'true';
        qsa('[data-pm-faq] > details', sec).forEach(function (d) {
          var mostra = cat === 'all'
            ? (!d.hasAttribute('data-pm-faq-extra') || aberto)
            : d.getAttribute('data-pm-faq-cat-item') === cat;
          d.classList.toggle('hidden', !mostra);
          if (!mostra) d.open = false;
        });
        if (mais) mais.classList.toggle('hidden', cat !== 'all');
      }
      qsa('[data-pm-faq-cat]', barra).forEach(function (chip) {
        chip.addEventListener('click', function () {
          qsa('[data-pm-faq-cat]', barra).forEach(function (c) { c.setAttribute('aria-selected', 'false'); });
          chip.setAttribute('aria-selected', 'true');
          aplica(chip.getAttribute('data-pm-faq-cat'));
        });
      });
    });

    /* ── Página seguinte já em casa ────────────────────────────────────
       O clique num link só começa a pedir o HTML no momento do clique —
       num telemóvel é ~1 s até haver alguma coisa no ecrã (medido a
       31-08-2026: 997 ms do clique ao DOM, com 4G e CPU travado). Aqui
       pede-se o HTML mal o dedo toca no link (ou o rato passa por cima):
       quando o clique acontece, a página já está na cache do browser.

       Regras: só links da própria loja, nada de carrinho/conta/checkout,
       no máximo 6 por página, e desliga-se em poupança de dados ou rede
       lenta. */
    (function () {
      var con = navigator.connection || {};
      if (con.saveData || /2g/.test(con.effectiveType || '')) return;
      var feitos = {}, contador = 0;
      function prefetch(a) {
        if (contador >= 6 || !a || !a.href) return;
        var u;
        try { u = new URL(a.href); } catch (e) { return; }
        if (u.origin !== location.origin || u.href === location.href) return;
        if (!/^\/(products|collections|pages|blogs)\//.test(u.pathname)) return;
        if (feitos[u.href]) return;
        feitos[u.href] = 1; contador++;
        var l = document.createElement('link');
        l.rel = 'prefetch'; l.href = u.href; l.as = 'document';
        document.head.appendChild(l);
      }
      var alvo = null;
      document.addEventListener('mouseover', function (e) {
        var a = e.target.closest && e.target.closest('a[href]');
        if (a && a !== alvo) { alvo = a; setTimeout(function () { if (alvo === a) prefetch(a); }, 90); }
      }, { passive: true, capture: true });
      /* ⚠️ No telemóvel, POUSAR o dedo num link é a forma normal de começar
         a fazer scroll — e isto puxava a página inteira a cada gesto desses.
         Agora exige-se intenção, como nos vídeos: 80 ms sem o dedo se
         mexer. Um toque a sério dura mais do que isso e continua a
         ganhar o avanço; um scroll move-se nos primeiros milímetros e
         cancela (medido a 01-09-2026). */
      var toqueA = null, toqueX = 0, toqueY = 0, toqueTmr = null;
      document.addEventListener('touchstart', function (e) {
        var a = e.target.closest && e.target.closest('a[href]');
        if (!a || !e.touches || !e.touches[0]) return;
        toqueA = a; toqueX = e.touches[0].clientX; toqueY = e.touches[0].clientY;
        clearTimeout(toqueTmr);
        toqueTmr = setTimeout(function () { if (toqueA === a) prefetch(a); }, 80);
      }, { passive: true, capture: true });
      document.addEventListener('touchmove', function (e) {
        if (!toqueA || !e.touches || !e.touches[0]) return;
        if (Math.abs(e.touches[0].clientX - toqueX) > 10 || Math.abs(e.touches[0].clientY - toqueY) > 10) {
          clearTimeout(toqueTmr); toqueA = null;
        }
      }, { passive: true, capture: true });
    })();

    /* Vídeos: src só quando se aproximam do ecrã; autoplay nos data-pm-autoplay.
       Os que tocam sozinhos param a um toque e continuam ao toque seguinte,
       e QUEM PÁRA MANDA: um vídeo parado à mão fica marcado e o observador
       já não o volta a arrancar quando se sai e se volta ao ecrã. A pausa
       do próprio observador (vídeo fora do ecrã) não conta como vontade —
       daí a marca `__pmObs`. Vale também para os que têm controlos
       nativos, porque a pausa deles chega aqui pelo mesmo evento. */
    /* A fonte do vídeo: no telemóvel, a rendição pequena quando existe. */
    function pmFonte(v) {
      var pequena = v.getAttribute('data-src-sm');
      var url = (pequena && window.innerWidth <= 699) ? pequena : v.dataset.src;
      /* arranque adiantado: o fragmento #t= faz o browser começar aí, sem mostrar o frame 0 */
      return v.__pmInicio ? url + '#t=' + v.__pmInicio : url;
    }
    function pmAuto(v) {
      return v.hasAttribute('data-pm-autoplay') || !!v.closest('[data-pm-ugc]');
    }
    function pmPausaObs(v) { v.__pmObs = true; v.pause(); }
    /* Nada arranca por passar de raspão: o vídeo tem de estar 0,4s quieto
       à vista para pedir o ficheiro. Num scroll rápido pela parede isso
       são ZERO bytes — antes bastava roçar o ecrã para puxar 8 MB. O
       clique não espera por nada (é `pmTocaOuPara`, que toca já). */
    var ESPERA_VIDEO = 400;
    function pmAgenda(v) {
      if (v.__tmr || !v.paused) return;
      v.__tmr = setTimeout(function () {
        v.__tmr = null;
        if (v.hasAttribute('data-pm-parado')) return;
        /* ⚠️ NADA ARRANCA COM UM PLAYER ABERTO — 05-09-2026. O observador não sabe
           que há um overlay por cima: com o player aberto continuava a mandar tocar
           os cartões da parede, que estão à vista para ele. Resultado: o clip SD do
           cartão a descarregar por trás, ao mesmo tempo que o player puxava o HD.
           Pausar no `aoAbrir` não chegava porque isto voltava a arrancá-los a
           seguir. */
        if (document.body.classList.contains('pm-player-open')) return;
        if (v.dataset.src && !v.src) v.src = pmFonte(v);
        v.play().catch(function () {});
      }, ESPERA_VIDEO);
    }
    function pmCancela(v) { if (v.__tmr) { clearTimeout(v.__tmr); v.__tmr = null; } }
    function pmSinal(v) {
      var b = v.parentElement && v.parentElement.querySelector('[data-pm-video-toggle]');
      if (b) b.classList.toggle('hidden', !v.hasAttribute('data-pm-parado'));
    }
    function pmTocaOuPara(v) {
      pmCancela(v);
      if (v.paused) {
        if (v.dataset.src && !v.src) v.src = pmFonte(v);
        v.play().catch(function () {});
      } else { v.pause(); }
    }
    function pmPrepararVideo(v) {
      var caixa = v.parentElement;
      if (!caixa || caixa.querySelector('[data-pm-video-toggle]')) return;
      caixa.classList.add('pm-video-hold');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pm-video-toggle hidden';
      b.setAttribute('data-pm-video-toggle', '');
      b.setAttribute('aria-label', (window.PM_I18N || {}).continuar_video || 'Continuar o vídeo');
      b.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15l13-7.5-13-7.5Z"/></svg>';
      caixa.appendChild(b);
      b.addEventListener('click', function (e) { e.stopPropagation(); pmTocaOuPara(v); });
      if (!v.controls) v.addEventListener('click', function () { pmTocaOuPara(v); });
      v.addEventListener('play', function () { v.removeAttribute('data-pm-parado'); pmSinal(v); });
      v.addEventListener('pause', function () {
        if (v.__pmObs) { v.__pmObs = false; }
        else if (v.ended && v.__pmInicio) { /* fim de uma volta com arranque adiantado: recomeça já no `ended`, não é a pessoa a parar */ }
        else { v.setAttribute('data-pm-parado', ''); }
        pmSinal(v);
      });
    }
    /* ── Um vídeo de cada vez na parede, no telemóvel ──────────────────
       Medido a 31-08-2026: a parede toca todos os cartões que estejam no
       ecrã e cada clipe são vários MB. Com dois ou três a transmitir ao
       mesmo tempo, a ligação fica cheia e as FOTOGRAFIAS da página ficam
       a meio de carregar. No telemóvel só toca o cartão mais visível —
       os outros nem chegam a pedir o ficheiro. No computador (mais
       largura de banda e quatro cartões à vista) mantém-se a parede toda
       viva, que é o efeito desenhado. */
    /* Medido a 01-09-2026 na ficha da Box, com o Chrome a sério: no COMPUTADOR tocavam
       CINCO cartões ao mesmo tempo e o browser puxava 22,9 MB de vídeo em seis segundos —
       sete vezes o peso da página inteira (3,5 MB), uns 30 Mbps sustidos. No telemóvel
       tocava um e eram 5,4 MB. Como os vídeos saem do MESMO domínio da loja, esses fluxos
       partilham a ligação com tudo o resto: carregava-se num link e o pedido do HTML da
       página seguinte ficava atrás deles. Era só no computador, e era por isto.
       Dois cartões mantêm a parede viva sem encher o cano. */
    var MAX_A_TOCAR = window.innerWidth <= 699 ? 1 : 2;
    function paredeReconcilia(parede) {
      var candidatos = qsa('video', parede).filter(function (v) { return (v.__ratio || 0) > 0.2; });
      candidatos.sort(function (a, b) { return (b.__ratio || 0) - (a.__ratio || 0); });
      var tocam = candidatos.slice(0, MAX_A_TOCAR);
      qsa('video', parede).forEach(function (v) {
        if (tocam.indexOf(v) >= 0) {
          if (v.hasAttribute('data-pm-parado')) return;
          pmAgenda(v);
        } else {
          pmCancela(v);
          if (!v.paused) pmPausaObs(v);
        }
      });
    }
    if ('IntersectionObserver' in window) {
      var vio = keep(new IntersectionObserver(function (entries) {
        var paredes = [];
        entries.forEach(function (e) {
          var v = e.target;
          v.__ratio = e.intersectionRatio;
          var parede = v.closest('[data-pm-ugc]');
          if (parede) {
            if (paredes.indexOf(parede) < 0) paredes.push(parede);
            return;                       /* quem toca decide-se lá em baixo */
          }
          if (e.isIntersecting) {
            if (pmAuto(v)) { pmAgenda(v); }
            else if (v.dataset.src && !v.src) { v.src = pmFonte(v); }
          } else {
            pmCancela(v);
            if (!v.paused) pmPausaObs(v);
          }
        });
        paredes.forEach(paredeReconcilia);
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] }));
      qsa('video[data-src]').forEach(function (v) {
        if (v.closest('[data-pm-press]')) return; /* a imprensa só toca ao clique */
        /* ── Arranque adiantado (dono, 03-09-2026: «o vídeo da Lara Afonso 0,5 s mais à
           frente, para não aparecer a parte preta») ── `data-start` em segundos, vindo do
           bloco. O primeiro arranque leva o fragmento #t= no URL (o browser começa aí e
           nem mostra o frame 0); as voltas seguintes já não podem ser o `loop` nativo, que
           volta sempre ao 0 e mostrava o negro outra vez: ouve-se o `ended` e recomeça-se
           no ponto escolhido. O `pause` que o fim dispara não conta como paragem da pessoa
           (ver pmPrepararVideo). */
        var ini = parseFloat(v.getAttribute('data-start') || '') || 0;
        if (ini > 0) {
          v.__pmInicio = ini;
          v.loop = false;
          v.addEventListener('ended', function () {
            try { v.currentTime = ini; } catch (e) { /* ainda sem metadados */ }
            v.play().catch(function () {});
          });
        }
        vio.observe(v);
        if (pmAuto(v)) {
          pmPrepararVideo(v);
          /* «Reduzir movimento» ligado no sistema: nada arranca sozinho —
             fica o cartaz e o botão de tocar, que é a escolha da pessoa. */
          if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            v.setAttribute('data-pm-parado', '');
            pmSinal(v);
          }
        }
        /* capa por cima até haver imagem a sério */
        var cover = v.parentElement && v.parentElement.querySelector('img[data-ugc-poster]');
        if (cover) v.addEventListener('timeupdate', function onT() {
          if (v.currentTime > 0.35) { cover.style.opacity = '0'; v.removeEventListener('timeupdate', onT); }
        });
      });
      /* ── Largar a ligação quando a pessoa vai sair ────────────────────
         Clicar num link não pára os vídeos: eles continuam a puxar bytes
         até a página nova comprometer, e o pedido do HTML dessa página vai
         atrás deles na mesma ligação. Ao primeiro sinal de saída pára-se
         tudo — a marca `__pmObs` garante que isto não conta como «a pessoa
         parou o vídeo», para o cartaz e o botão de tocar não aparecerem
         no meio da navegação. */
      function pmLargarVideos() {
        qsa('video').forEach(function (v) {
          pmCancela(v);
          if (!v.paused) { v.__pmObs = true; v.pause(); }
        });
      }
      /* ⚠️ Na fase de CAPTURA o `defaultPrevented` é sempre falso — ainda
         ninguém teve hipótese de o marcar. Um link que abra um modal em vez
         de navegar deixava a parede parada para sempre. Ouve-se na subida,
         onde já se sabe se a navegação vai mesmo acontecer. */
      var tmrRetoma = null;
      function pmRetomaSeFicarmos() {
        clearTimeout(tmrRetoma);
        tmrRetoma = setTimeout(function () {
          if (document.visibilityState !== 'visible') return;
          qsa('[data-pm-ugc]').forEach(paredeReconcilia);
        }, 1500);
      }
      document.addEventListener('click', function (e) {
        var a = e.target && e.target.closest && e.target.closest('a[href]');
        if (!a || a.target === '_blank' || e.defaultPrevented) return;
        var href = a.getAttribute('href') || '';
        if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript):/i.test(href)) return;
        pmLargarVideos();
        /* Cinto e suspensórios: se ao fim de 1,5s ainda cá estivermos, a
           navegação não aconteceu e a parede volta ao que estava. */
        pmRetomaSeFicarmos();
      });
      document.addEventListener('submit', pmLargarVideos);
      window.addEventListener('pagehide', pmLargarVideos);

      qsa('video[data-pm-lazy-video]').forEach(function (v) {
        v.addEventListener('play', function () { if (v.dataset.src && !v.src) { v.src = v.dataset.src; v.play().catch(function () {}); } }, { once: true });
      });
    }

    /* 07-09: os cartões cujo clipe não pode dar som perdem o botão de som e ficam
       marcados. Tirar o botão (em vez de o deixar a não fazer nada) é o que impede o
       `mouseenter` de o clicar e evita um altifalante no ecrã que mente ao visitante. */
    qsa('[data-pm-ugc-card]').forEach(function (card) {
      if (!eMudo(card, urlDoCartao(card))) return;
      card.setAttribute('data-mudo', '1');
      var v = qs('video', card); if (v) v.muted = true;
      var b = qs('[data-pm-ugc-sound]', card); if (b) b.remove();
    });

    /* UGC: som de UM cartão de cada vez */
    qsa('[data-pm-ugc]').forEach(function (wall) {
      var cards = qsa('[data-pm-ugc-sound]', wall);
      cards.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var myVideo = btn.parentElement.querySelector('video');
          var turningOn = myVideo && myVideo.muted;
          qsa('video', wall).forEach(function (v) { v.muted = true; });
          cards.forEach(function (o) {
            o.setAttribute('aria-pressed', 'false');
            qs('[data-pm-ugc-icon-on]', o).classList.add('hidden');
            qs('[data-pm-ugc-icon-off]', o).classList.remove('hidden');
          });
          if (turningOn && myVideo) {
            if (myVideo.dataset.src && !myVideo.src) myVideo.src = myVideo.dataset.src;
            myVideo.muted = false;
            myVideo.play().catch(function () {});
            btn.setAttribute('aria-pressed', 'true');
            qs('[data-pm-ugc-icon-on]', btn).classList.remove('hidden');
            qs('[data-pm-ugc-icon-off]', btn).classList.add('hidden');
          }
        });
      });
    });

    /* Imprensa: o vídeo só carrega ao clique */
    qsa('[data-pm-press]').forEach(function (box) {
      var play = qs('[data-pm-press-play]', box);
      var video = qs('video', box);
      if (!play || !video) return;
      play.addEventListener('click', function () {
        if (video.dataset.src && !video.src) video.src = pmFonte(video);
        video.controls = true;
        video.play().catch(function () {});
        play.remove();
      });
    });

    /* Lightbox simples (fotos de clientes) */
    qsa('[data-pm-lightbox]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-pm-lightbox');
        var wrap = document.createElement('div');
        wrap.innerHTML =
          '<div class="fixed inset-0 z-overlay bg-ink/85"></div>' +
          '<div class="fixed inset-0 z-overlay-panel flex items-center justify-center p-4" role="dialog" aria-modal="true">' +
          '<button type="button" class="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Fechar">' +
          '<svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M6 18 18 6M6 6l12 12"/></svg></button>' +
          '<img src="' + url + '" alt="" class="max-w-full max-h-full object-contain rounded-lg" decoding="async"></div>';
        function close() { wrap.remove(); lockScroll(false); }
        wrap.addEventListener('click', close);
        lockScroll(true);
        document.body.appendChild(wrap);
      });
    });
  }

  /* ── Altura do cabeçalho → --pm-header-h ─────────────────────────
        Quem encosta ao cabeçalho (a barra de filtros/ordenação) precisa
        de saber a altura REAL dele: a barra de anúncio pode ser fechada
        e o valor fixo que aqui estava deixava um vão a flutuar. Mede-se
        no arranque, ao redimensionar e quando o anúncio desaparece. */
  function initHeaderHeight() {
    var header = qs('[data-pm-header]');
    if (!header) return;
    var toolbar = qs('.pm-under-header');
    function medir() {
      var h = Math.round(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--pm-header-h', h + 'px');
      if (toolbar) {
        document.documentElement.style.setProperty('--pm-toolbar-h', Math.round(toolbar.getBoundingClientRect().height) + 'px');
      }
    }
    medir();
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(medir);
      ro.observe(header);
      if (toolbar) ro.observe(toolbar);
      keep(ro);
    }
    window.addEventListener('resize', medir, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(medir).catch(function () {});
  }

  /* ── Arranque ────────────────────────────────────────────────────── */
  /* ── Mudar de língua (rodapé) ────────────────────────────────────
     O `<select>` está dentro do form de localização do Shopify: sem
     JavaScript, muda-se com o botão «Mudar». Com JavaScript, escolher
     basta — e o botão sai da frente, que já não faz falta. */
  function initIdioma() {
    var sel = qs('[data-pm-lang]');
    if (!sel) return;
    var ok = qs('[data-pm-lang-ok]');
    if (ok) ok.hidden = true;
    sel.addEventListener('change', function () {
      var form = sel.closest('form');
      if (form) form.submit();
    });
  }

  /* ── A fotografia de trás dos cartões ────────────────────────────
     Só se descarrega onde existe rato. No telemóvel a troca no hover
     nunca acontece e a imagem era peso puro. Corre uma vez e volta a
     correr quando entram cartões novos (rails, filtros). */
  function initHover() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    qsa('img[data-pm-hover-src]').forEach(function (im) {
      im.src = im.getAttribute('data-pm-hover-src');
      im.removeAttribute('data-pm-hover-src');
    });
  }
  window.PMHover = initHover;

  /* ── Klarna «3 pagamentos sem juros»: cantos redondos (dono, 04-09-2026) ──
     O selo é um <klarna-placement> com shadow DOM aberto e o desenho no CSS
     deles (`.container{border-radius:5px;border:1px solid #f0eeeb;padding:4px 8px}`);
     o nosso CSS não entra lá. Injeta-se uma folha DENTRO do shadow root
     (raio 12 px como os botões da ficha, um pouco mais de ar) e repõe-se se a
     Klarna redesenhar (o montante muda com a variante). Se um dia o shadow
     root vier fechado, não acontece nada — nem erro. */
  /* A linha «3× de … sem juros» é NOSSA e sai desenhada do servidor (0 ms). O que
     continua a ser da Klarna é a DIVULGAÇÃO LEGAL: o componente deles fica na
     página escondido à vista e o nosso «Saber mais» carrega no botão que vive
     dentro do shadow root dele. Assim ninguém copia o texto legal e ele nunca
     fica desactualizado.
     ⚠️ O botão nasce escondido e só aparece quando o deles for mesmo encontrado:
     um «Saber mais» que não abre nada é pior do que não o haver. */
  function initPrestacoes() {
    var meu = qs('[data-pm-prest-mais]');
    if (!meu) return;
    function deles() {
      var kp = qs('klarna-placement');
      return kp && kp.shadowRoot ? kp.shadowRoot.querySelector('button.link, a.link') : null;
    }
    var n = 0;
    (function espera() {
      if (deles()) { meu.hidden = false; return; }
      if (++n < 60) setTimeout(espera, 250);
    })();
    meu.addEventListener('click', function () {
      var b = deles();
      if (b) b.click();
    });
  }

  function initKlarna() {
    var CSS = '.container{border-radius:.75rem !important;padding:8px 12px !important}';
    // ⚠️ Dentro do bloco de pagamento a Klarna deixa de ser uma CAIXA e passa a ser
    // uma linha: a caixa dela (fundo branco, borda #eae7e4, raio 12px) era o
    // terceiro retângulo seguido debaixo do botão. O CSS vive no shadow root, que
    // não vê as classes do documento — por isso a escolha faz-se aqui, em JS.
    // A mensagem da Klarna não é um bloco: é uma SEGUNDA LEITURA DO PREÇO
    // («399 € ou 3 × 133 €»). Por isso perde a caixa e encolhe para corpo de
    // linha secundária. O selo fica nos 52×22 (acima do mínimo da marca deles).
    // ⚠️ `justify-content` vem de fora por variável CSS: as custom properties
    // atravessam o shadow root, as classes do documento não.
    var CSS_NUA = '.container{border:0 !important;background:transparent !important;padding:0 !important;'
      + 'border-radius:0 !important;gap:8px !important;align-items:center !important;'
      + 'justify-content:var(--pm-k-just, center) !important}'
      + '.badge-container,.badge{width:var(--pm-k-badge, 44px) !important;height:var(--pm-k-badge-h, 18px) !important}'
      + '.badge svg{width:var(--pm-k-badge, 44px) !important;height:var(--pm-k-badge-h, 18px) !important}'
      + '.text-wrapper{font-size:var(--pm-k-fs, 12px) !important;line-height:1.3 !important}'
      // com pouca largura, o logótipo passa para CIMA do texto: assim a frase usa a
      // largura toda da caixa e ganha-se mais em linhas do que se perde em altura.
      + '@media(max-width:560px){.container{flex-direction:var(--pm-k-dir, row) !important;'
      + 'align-items:flex-start !important;gap:var(--pm-k-gap, 6px) !important}}';
    function pinta(kp) {
      var root = kp.shadowRoot; if (!root) return false;
      if (!root.querySelector('style[data-pm]')) {
        var st = document.createElement('style'); st.setAttribute('data-pm', '');
        st.textContent = kp.closest('.pm-klarna-nua') ? CSS_NUA : CSS;
        root.appendChild(st);
      }
      // ⚠️ A caixa cinzenta só se pinta DEPOIS de haver mensagem. É desenho nosso à
      // volta de conteúdo deles, que pode nunca chegar (bloqueador, consentimento,
      // Klarna em baixo, país não elegível) — e uma pastilha cinzenta vazia ao lado
      // do preço é pior do que não haver nada. Lê-se o <p> deles, não o textContent
      // da raiz: essa traz o CSS que acabámos de injectar e dava sempre «cheia».
      var w = kp.closest('.pm-klarna-nua');
      if (w) {
        var par = root.querySelector('p');
        w.classList.toggle('is-cheia', !!(par && par.textContent.trim().length > 2));
      }
      return true;
    }
    function liga(kp) {
      if (kp.__pm) return; kp.__pm = true;
      var n = 0;
      // ⚠️ `pinta` devolve true assim que há shadow root, mas a MENSAGEM pode chegar
      // depois — por isso insiste-se um pouco mais mesmo com o estilo já posto, para
      // a classe `is-cheia` apanhar o texto quando ele aparecer.
      (function tenta() { var ok = pinta(kp); if ((ok && n > 12) || ++n > 40) return; setTimeout(tenta, 250); })();
      (function vigia() {
        if (!kp.shadowRoot) { if (++n < 80) setTimeout(vigia, 250); return; }
        // ⚠️ `subtree: true`: a mensagem deles não nasce como filha directa da raiz
        // do shadow — vem dentro de divs. Só com os filhos directos o observador
        // nunca via o <p> aparecer, e a caixa cinzenta nunca se pintava mesmo com a
        // Klarna a responder (medido em fr: altura 56, `is-cheia` ausente).
        new MutationObserver(function () { pinta(kp); }).observe(kp.shadowRoot, { childList: true, subtree: true });
      })();
    }
    qsa('klarna-placement').forEach(liga);
    if (!('MutationObserver' in window)) return;
    new MutationObserver(function (ms) {
      ms.forEach(function (m) {
        m.addedNodes.forEach(function (nd) {
          if (nd.nodeType !== 1) return;
          if (nd.matches && nd.matches('klarna-placement')) liga(nd);
          qsa('klarna-placement', nd).forEach(liga);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ── O player em ecrã inteiro, partilhado (04-09-2026) ──────────────────
     Um só motor para a parede «em ação» (clips 9:16) e para a banda «Na
     televisão» (16:9, `pm-player--wide`). Recebe os itens {hd, sd, poster,
     who, cap, start} e trata de tudo: som, segmentos como nas stories,
     deslizar/setas/teclas, cartaz + anel enquanto carrega, queda para a
     rendição leve se a pesada falhar, bloqueio de scroll que o iOS respeita,
     botão «voltar» a fechar, foco, separador escondido, «Adicionar» (PMCart). */
  /* ── VÍDEOS SEM SOM ──────────────────────────────────────────────────
     O clipe da Fountain a dar água tinha uma faixa de voz que não é nossa (medida:
     média −38,9 dB, pico −17,5). A 07-09-2026 silenciou-se por código; à noite o dono
     mandou resolver na origem: o ficheiro foi descarregado, o áudio removido SEM
     recodificar a imagem (`-c:v copy -an` — o fluxo de vídeo ficou bit a bit igual,
     mesmo MD5) e recarregado no Shopify. O ID mudou e o novo ficheiro NÃO TEM faixa
     de áudio nenhuma.

     Então porque é que a lista continua? Porque o cartão mostra um botão de som e,
     num vídeo sem áudio, esse botão não faz nada — um controlo morto é pior do que
     controlo nenhum. Estar aqui é o que o REMOVE. Já não é uma questão de silenciar:
     é de não prometer som onde não há.
     A regra fica ancorada no ID e não no markup porque os `snippets/pm-landing-*` são
     GERADOS pelo `gera-landings.py` e um dia voltam a ser gerados. O cartão também
     respeita `data-mudo`, para o gerador o poder dizer quando voltar a correr. */
  var VIDEOS_MUDOS = ['b109acd7705442c1aa6022d65aa55a8a'];   /* Fountain a dar água — sem faixa de áudio desde 07-09-2026 */
  function eMudo(el, url) {
    if (el && el.closest && el.closest('[data-mudo]')) return true;
    var u = url || '';
    for (var i = 0; i < VIDEOS_MUDOS.length; i++) if (u.indexOf(VIDEOS_MUDOS[i]) !== -1) return true;
    return false;
  }
  function urlDoCartao(card) {
    var v = card && card.querySelector ? card.querySelector('video') : null;
    return (card && card.getAttribute ? (card.getAttribute('data-hd') || '') : '') + ' ' +
           (card && card.getAttribute ? (card.getAttribute('data-sd') || '') : '') + ' ' +
           ((v && v.dataset && v.dataset.src) || '');
  }

  function montaPlayer(player, itens, opts) {
    opts = opts || {};
    var pv = qs('[data-pm-player-video]', player), segs = qs('[data-pm-player-segs]', player);
    var who = qs('[data-pm-player-who]', player), cap = qs('[data-pm-player-cap]', player);
    var prev = qs('[data-pm-player-prev]', player), next = qs('[data-pm-player-next]', player), add = qs('[data-pm-player-add]', player);
    var idx = -1, aberto = false, scrollGuardado = 0, elOrigem = null;
    itens.forEach(function () { var sg = document.createElement('i'); sg.appendChild(document.createElement('b')); segs.appendChild(sg); });
    if (itens.length < 2) segs.hidden = true;
    function mostra(i) {
      idx = Math.max(0, Math.min(itens.length - 1, i));
      var it = itens[idx];
      pv.poster = it.poster || '';
      player.classList.add('is-loading');
      qsa('i', segs).forEach(function (sg, k) { qs('b', sg).style.width = k < idx ? '100%' : '0'; });
      who.innerHTML = it.who || '';
      cap.textContent = it.cap || '';
      requestAnimationFrame(alturaRodape);
      if (prev) prev.disabled = idx === 0;
      if (next) next.disabled = idx === itens.length - 1;
      pv.pause();
      /* ⚠️ LIBERTAR O ANTERIOR — 05-09-2026, dono: «quando clico num vídeo e vou
         para outro, aquilo fica em fila a carregar totalmente o que estou até ir
         para o outro». Atribuir um `src` novo aborta o pedido em curso, mas só
         quando o browser chega lá; tirar o atributo e chamar `load()` corta já,
         e liberta o buffer do clip que ficou para trás. */
      if (pv.getAttribute('src')) {
        pv.removeAttribute('src');
        try { pv.load(); } catch (e) {}
      }
      pv.__pmLeve = false;
      pv.src = (it.hd || it.sd || '') + (it.start ? '#t=' + it.start : '');
      pv.muted = !!it.mudo || eMudo(null, (it.hd || '') + ' ' + (it.sd || ''));
      pv.play().catch(function () { pv.muted = true; pv.play().catch(function () {}); });
    }
    /* ⚠️ A FORMA DO VÍDEO, medida — 05-09-2026. O CSS tinha `aspect-ratio: 16/9`
       fixo, mas o mesmo player mostra vídeos de todos os feitios: os verticais
       da parede (9:16), o quadrado do Pillar (720×720) e a peça da SIC (16:9).
       Um quadrado dentro de uma caixa 16:9 ficava com 219 px de altura num
       telemóvel de 390 e barras pretas dos dois lados — o dono: «tem barras
       pretas e aparece o texto do add to cart em cima do vídeo». O «add to
       cart» é o cartão de produto do PRÓPRIO rodapé do player, que ficava a
       flutuar no preto por baixo de um vídeo pequeno demais.
       Duas variáveis porque o CSS precisa das duas formas: `--pm-vid-ar` para
       o `aspect-ratio` e `--pm-vid-arn` (número) para as contas de largura. */
    var rodape = qs('.pm-player__foot', player);
    function formaDoVideo() {
      if (!pv.videoWidth || !pv.videoHeight) return;
      player.style.setProperty('--pm-vid-ar', pv.videoWidth + ' / ' + pv.videoHeight);
      player.style.setProperty('--pm-vid-arn', (pv.videoWidth / pv.videoHeight).toFixed(4));
    }
    /* ⚠️ A ALTURA DO RODAPÉ, medida — 05-09-2026, dono: «ainda tapa a parte
       final do vídeo no desktop, vê a forma mais inteligente de nunca ter esse
       problema». A mais inteligente é o rodapé NÃO estar por cima: tem faixa
       própria e o vídeo encolhe o que for preciso para os dois caberem. Para
       isso o CSS precisa de saber quanto ele ocupa — e ocupa coisas
       diferentes conforme a legenda tem uma linha ou três, por isso mede-se
       a cada vídeo em vez de se adivinhar um valor. */
    function alturaRodape() {
      if (!rodape) return;
      player.style.setProperty('--pm-foot-h', (rodape.offsetHeight || 0) + 'px');
    }
    window.addEventListener('resize', function () { formaDoVideo(); alturaRodape(); });
    pv.addEventListener('loadedmetadata', formaDoVideo);
    pv.addEventListener('resize', formaDoVideo);
    pv.addEventListener('playing', function () { formaDoVideo(); player.classList.remove('is-loading'); });
    pv.addEventListener('waiting', function () { player.classList.add('is-loading'); });
    pv.addEventListener('error', function () {
      var it = itens[idx];
      if (it && it.sd && !pv.__pmLeve && it.sd !== it.hd) { pv.__pmLeve = true; pv.src = it.sd; pv.play().catch(function () {}); }
      else player.classList.remove('is-loading');
    });
    document.addEventListener('visibilitychange', function () {
      if (!aberto) return;
      if (document.visibilityState === 'hidden') pv.pause(); else pv.play().catch(function () {});
    });
    pv.addEventListener('timeupdate', function () {
      var sg = qsa('i', segs)[idx];
      if (sg && pv.duration) qs('b', sg).style.width = (pv.currentTime / pv.duration * 100) + '%';
    });
    pv.addEventListener('ended', function () {
      if (opts.avanca !== false && idx < itens.length - 1) mostra(idx + 1); else pedeFecho();
    });
    function abre(i, origem) {
      aberto = true;
      elOrigem = origem || null;
      scrollGuardado = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = (-scrollGuardado) + 'px';
      document.body.classList.add('pm-player-open');
      player.hidden = false;
      if (opts.aoAbrir) opts.aoAbrir();
      try { history.pushState({ pmPlayer: true }, ''); } catch (e) {}
      mostra(i);
      var x = qs('[data-pm-player-close]:not(.pm-player__bg)', player);
      if (x && x.focus) x.focus({ preventScroll: true });
    }
    function fecha() {
      if (!aberto) return;
      aberto = false;
      pv.pause();
      pv.removeAttribute('src');
      pv.removeAttribute('poster');
      try { pv.load(); } catch (e) {}
      player.hidden = true;
      player.classList.remove('is-loading');
      document.body.classList.remove('pm-player-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollGuardado);
      if (opts.aoFechar) opts.aoFechar();
      if (elOrigem && elOrigem.focus) elOrigem.focus({ preventScroll: true });
    }
    function pedeFecho() {
      if (!aberto) return;
      if (history.state && history.state.pmPlayer) history.back(); else fecha();
    }
    window.addEventListener('popstate', function () { if (aberto) fecha(); });
    qsa('[data-pm-player-close]', player).forEach(function (b) { b.addEventListener('click', pedeFecho); });
    if (prev) prev.addEventListener('click', function () { if (idx > 0) mostra(idx - 1); });
    if (next) next.addEventListener('click', function () { if (idx < itens.length - 1) mostra(idx + 1); });
    var deslizou = 0;
    pv.addEventListener('click', function (e) {
      if (Date.now() - deslizou < 500) return;
      var r = pv.getBoundingClientRect(), x = (e.clientX - r.left) / r.width;
      if (itens.length > 1 && x < 0.3) { if (idx > 0) mostra(idx - 1); }
      else if (itens.length > 1 && x > 0.7) { if (idx < itens.length - 1) mostra(idx + 1); else pedeFecho(); }
      else if (pv.paused) pv.play().catch(function () {}); else pv.pause();
    });
    var t0 = null;
    player.addEventListener('touchstart', function (e) { var t = e.touches[0]; t0 = { x: t.clientX, y: t.clientY, n: e.touches.length }; }, { passive: true });
    player.addEventListener('touchend', function (e) {
      if (!t0) return;
      var t = e.changedTouches[0], dx = t.clientX - t0.x, dy = t.clientY - t0.y, multi = t0.n > 1;
      t0 = null;
      if (multi) return;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        deslizou = Date.now();
        if (dx < 0 && idx < itens.length - 1) mostra(idx + 1);
        else if (dx > 0 && idx > 0) mostra(idx - 1);
      } else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) { deslizou = Date.now(); pedeFecho(); }
    }, { passive: true });
    document.addEventListener('keydown', function (e) {
      if (!aberto) return;
      if (e.key === 'Escape') pedeFecho();
      else if (e.key === 'ArrowRight') { if (idx < itens.length - 1) mostra(idx + 1); }
      else if (e.key === 'ArrowLeft') { if (idx > 0) mostra(idx - 1); }
      else if (e.key === ' ') { e.preventDefault(); if (pv.paused) pv.play().catch(function () {}); else pv.pause(); }
    });
    if (add) add.addEventListener('click', function () {
      var id = add.getAttribute('data-pm-player-add');
      if (!id || !window.PMCart) return;
      add.setAttribute('aria-busy', 'true');
      window.PMCart.add(id, 1).then(function () { pedeFecho(); }).catch(function () {}).finally(function () { add.removeAttribute('aria-busy'); });
    });
    return { abre: abre, fecha: fecha };
  }

  /* ── A parede «em ação»: pontos e cartão activo no telemóvel, barra de
     progresso, som só no cartão activo, som ao passar o rato no computador,
     e o toque no cartão a abrir o player com os clips a 720p. ── */
  function initUgcPlus() {
    var fino = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    var ultimoToque = 0;
    document.addEventListener('touchstart', function () { ultimoToque = Date.now(); }, { passive: true });
    qsa('[data-pm-ugc]').forEach(function (wall) {
      var cards = qsa('[data-pm-ugc-card]', wall);
      if (!cards.length) return;
      var sec = wall.closest('section') || wall.parentElement;
      var player = qs('[data-pm-ugc-player]', sec);
      var dots = qs('[data-pm-ugc-dots]', sec);
      if (dots) cards.forEach(function () { dots.appendChild(document.createElement('i')); });
      function silencia(card) {
        var v = qs('video', card), btn = qs('[data-pm-ugc-sound]', card);
        if (v) v.muted = true;
        if (btn) {
          btn.setAttribute('aria-pressed', 'false');
          var on = qs('[data-pm-ugc-icon-on]', btn), off = qs('[data-pm-ugc-icon-off]', btn);
          if (on) on.classList.add('hidden');
          if (off) off.classList.remove('hidden');
        }
      }
      var activo = -1;
      function marcaActivo() {
        var c0 = cards[0];
        var gap = parseFloat(getComputedStyle(wall).columnGap) || 12;
        var i = Math.round(wall.scrollLeft / (c0.offsetWidth + gap));
        i = Math.max(0, Math.min(cards.length - 1, i));
        if (i === activo) return;
        activo = i;
        cards.forEach(function (c, k) {
          c.classList.toggle('is-active', k === i);
          if (k !== i) silencia(c);
        });
        if (dots) qsa('i', dots).forEach(function (d, k) { d.classList.toggle('is-on', k === i); });
      }
      marcaActivo();
      var agendado = false;
      wall.addEventListener('scroll', function () {
        if (agendado) return;
        agendado = true;
        requestAnimationFrame(function () { agendado = false; marcaActivo(); });
      }, { passive: true });
      cards.forEach(function (card) {
        var v = qs('video', card), bar = qs('.pm-ugc-bar i', card);
        if (v && bar) v.addEventListener('timeupdate', function () {
          if (v.duration) bar.style.width = (v.currentTime / v.duration * 100) + '%';
        });
        if (fino) {
          var btn = qs('[data-pm-ugc-sound]', card);
          if (btn && v) {
            card.addEventListener('mouseenter', function () { if (Date.now() - ultimoToque < 1200) return; if (v.muted) btn.click(); });
            card.addEventListener('mouseleave', function () { if (!v.muted) btn.click(); });
          }
        }
      });
      if (!player) return;
      var aTocar = [];
      var itens = cards.map(function (card) {
        var v0 = qs('video', card), cz = qs('img[data-ugc-poster]', card), cred = qs('.pm-ugc-credit', card), c = qs('.pm-ugc-cap', card);
        return { hd: card.getAttribute('data-hd') || (v0 && v0.dataset.src) || '', sd: v0 && v0.dataset.src, poster: cz ? (cz.currentSrc || cz.src) : '', who: cred ? cred.innerHTML : '', cap: c ? c.textContent : '', start: parseFloat(card.getAttribute('data-start') || '') || 0, mudo: eMudo(card, urlDoCartao(card)) };
      });
      var pl = montaPlayer(player, itens, {
        aoAbrir: function () {
          aTocar = qsa('video', wall).filter(function (v) { return !v.paused; });
          /* ⚠️ PAUSAR NÃO CHEGA — 05-09-2026. Um <video> em pausa com `src` continua
             a encher o buffer: por trás do player, invisível, a parede continuava a
             puxar o clip SD do cartão ao mesmo tempo que o player puxava o HD. Dois
             descarregamentos do MESMO vídeo, e é o que o dono sentia na ficha da Box,
             que tem seis. Tirar o `src` corta mesmo; guarda-se para repor ao fechar. */
          aTocar.forEach(function (v) {
            v.__pmObs = true;
            v.pause();
            v.__pmSrc = v.getAttribute('src') || '';
            if (v.__pmSrc) { v.removeAttribute('src'); try { v.load(); } catch (e) {} }
          });
          cards.forEach(silencia);
        },
        aoFechar: function () {
          aTocar.forEach(function (v) {
            if (v.__pmSrc) { v.setAttribute('src', v.__pmSrc); v.__pmSrc = ''; }
            v.play().catch(function () {});
          });
          aTocar = [];
        }
      });
      cards.forEach(function (card, i) {
        card.addEventListener('click', function (e) {
          if (e.target.closest('[data-pm-ugc-sound]') || e.target.closest('[data-pm-video-toggle]')) return;
          e.preventDefault();
          e.stopPropagation();
          pl.abre(i, card);
        }, true);
        card.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pl.abre(i, card); } });
      });
    });
  }

  /* ── A banda «Na televisão»: o corte de 44 s e a peça completa, no mesmo
     player, em 16:9. Sem <video> na página até se carregar. ── */
  function initPress() {
    qsa('[data-pm-press], [data-pm-loop-frame]').forEach(function (frame) {
      var sec = frame.closest('section') || frame.parentElement;
      var player = qs('[data-pm-press-player]', sec);
      if (!player) return;
      var quote = qs('.pm-press__quote', sec);
      var whoHtml = '';   /* sem selo: o logótipo da SIC está no próprio vídeo, no canto */
      var itens = [{ hd: frame.getAttribute('data-hd'), sd: frame.getAttribute('data-sd'), poster: frame.getAttribute('data-poster'), who: whoHtml, cap: quote ? quote.textContent : '', mudo: eMudo(frame, urlDoCartao(frame)) }];
      if (frame.getAttribute('data-full-hd')) itens.push({ hd: frame.getAttribute('data-full-hd'), sd: frame.getAttribute('data-full-sd'), poster: frame.getAttribute('data-poster'), who: whoHtml, cap: '' });
      var pl = montaPlayer(player, itens, { avanca: false });
      qsa('[data-pm-press-open]', sec).forEach(function (b) {
        b.addEventListener('click', function () {
          var i = parseInt(b.getAttribute('data-pm-press-open') || '0', 10) || 0;
          pl.abre(Math.min(i, itens.length - 1), b);
        });
      });
    });
  }

  /* ── Os números da conta «7 min → +40 h» contam de zero quando entram no ecrã
     (04-09-2026): uma vez só, ~0,9 s, sem animação com «reduzir movimento». ── */
  function initContadores() {
    var alvos = qsa('.pm-ba__s b, .pm-safe__num b');
    if (!alvos.length || !('IntersectionObserver' in window)) return;
    var reduz = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    alvos.forEach(function (b) {
      var m = /^(\D*)(\d+)(.*)$/.exec(b.textContent.trim());
      if (!m) return;
      var pre = m[1], fim = parseInt(m[2], 10), pos = m[3], feito = false;
      keep(new IntersectionObserver(function (es, io) {
        es.forEach(function (e) {
          if (!e.isIntersecting || feito) return;
          feito = true; io.disconnect();
          if (reduz) return;
          var t0 = performance.now(), dur = 900;
          (function passo(t) {
            var k = Math.min(1, (t - t0) / dur), ease = 1 - Math.pow(1 - k, 3);
            b.textContent = pre + Math.round(fim * ease) + pos;
            if (k < 1) requestAnimationFrame(passo);
          })(t0);
        });
      }, { threshold: 0.6 })).observe(b);
    });
  }

  /* ── «Detalhes do Produto»: navegação lateral no computador (há sempre um
     aberto), acordeão no telemóvel (04-09-2026). O estado é a classe
     `is-open` + aria-expanded + o `hidden` do painel. ── */
  function initDet() {
    var mq = window.matchMedia('(min-width: 1024px)');
    qsa('[data-pm-det]').forEach(function (box) {
      var items = qsa('[data-pm-det-item]', box);
      function abre(alvo, on) {
        items.forEach(function (o) {
          var e = o === alvo && on;
          o.classList.toggle('is-open', e);
          var b = qs('.pm-det__tab', o), p = qs('.pm-det__panel', o);
          if (b) b.setAttribute('aria-expanded', String(e));
          if (p) p.hidden = !e;
        });
      }
      items.forEach(function (it) {
        var btn = qs('.pm-det__tab', it);
        if (!btn) return;
        btn.addEventListener('click', function () {
          var fechar = it.classList.contains('is-open');
          if (fechar && mq.matches) return;          // no computador há sempre um aberto
          abre(it, !fechar);
        });
      });
      function garante() {
        if (mq.matches && !items.some(function (o) { return o.classList.contains('is-open'); }) && items[0]) abre(items[0], true);
      }
      if (mq.addEventListener) mq.addEventListener('change', garante); else if (mq.addListener) mq.addListener(garante);
      if (!mq.matches) abre(null, false);
    });
  }

  /* ── «Pergunte à PetMaison» (04-09-2026): responde SEM IA e sem servidor.
     Indexa o que está nesta página (perguntas + respostas do FAQ e a ficha
     técnica dos «Detalhes»), normaliza (sem acentos, sem pontuação, radicais
     de 5 letras, sinónimos por língua) e devolve a que bate melhor — com o
     texto que já está escrito. Sem resposta, «Fale connosco». ── */
  var ASK_STOP = {
    pt: 'a o os as de do da dos das e em um uma que se nao para com por como qual quais quanto quanta quantos quantas tem ter ser posso pode podem meu minha meus minhas seu sua seus suas isto isso este esta ele ela eles elas ao no na nos nas ou mas ja mais muito muita muitos so tambem sim vai vou ha existe existem eu tu voces ele ela sobre entre ate onde quando porque',
    en: 'the a an is are it i my of to for with in on and or can do does how what which be there any this that these those you your we our will would should could was were has have had if',
    es: 'el la los las de del un una que se no para con por como cual cuales cuanto cuantos tiene tener ser puedo puede mi mis su sus es y o al en lo esto eso este esta hay',
    fr: 'le la les de des du un une que qui se ne pas pour avec par comment quel quelle quels combien est sont peut peux mon ma mes son sa ses et ou au aux en il elle je vous nous ce cette ces y a'
  };
  var ASK_SIN = {
    pt: { barulho: 'ruido', barulhento: 'ruido', som: 'ruido', silencioso: 'ruido', silenciosa: 'ruido', silencio: 'ruido', cheiro: 'odor', cheiros: 'odor', cheira: 'odor', odores: 'odor', telemovel: 'aplicacao', app: 'aplicacao', smartphone: 'aplicacao', wifi: 'aplicacao', internet: 'aplicacao', bebe: 'gatinho', bebes: 'gatinho', cria: 'gatinho', crias: 'gatinho', filhote: 'gatinho', kitten: 'gatinho', preco: 'custa', custo: 'custa', custos: 'custa', caro: 'custa', cara: 'custa', dinheiro: 'custa', portes: 'envio', entrega: 'envio', entregam: 'envio', enviam: 'envio', prazo: 'envio', chega: 'envio', demora: 'envio', tomada: 'corrente', eletricidade: 'corrente', electricidade: 'corrente', bateria: 'corrente', pilhas: 'corrente', ficha: 'corrente', devolver: 'devolucao', devolucoes: 'devolucao', reembolso: 'devolucao', trocar: 'devolucao', troca: 'devolucao', arrependo: 'devolucao', avaria: 'garantia', avariar: 'garantia', estraga: 'garantia', estragar: 'garantia', parte: 'garantia', partir: 'garantia', tamanho: 'dimensoes', medidas: 'dimensoes', mede: 'dimensoes', grande: 'dimensoes', espaco: 'apartamento', pequena: 'apartamento', pequeno: 'apartamento', cao: 'animais', caes: 'animais', coelho: 'animais', coelhos: 'animais', pesa: 'peso', pesado: 'peso', pesada: 'peso', kg: 'peso', quilos: 'peso', instalar: 'montar', instalacao: 'montar', montagem: 'montar', configurar: 'montar', prestacoes: 'pagar', klarna: 'pagar', mbway: 'pagar', pagamento: 'pagar', cartao: 'pagar', paypal: 'pagar', saco: 'sacos', lavar: 'limpeza', limpar: 'limpeza', limpa: 'limpeza', esvazio: 'esvaziar', deposito: 'esvaziar', silica: 'areia', tofu: 'areia', bentonite: 'areia', aglomerante: 'areia', areias: 'areia', adaptar: 'adapta', habituar: 'adapta', habitua: 'adapta', aceita: 'adapta', medo: 'adapta', dois: 'gatos', tres: 'gatos', varios: 'gatos', gato: 'gatos', gata: 'gatos', gatas: 'gatos', decoracao: 'decoracao', design: 'decoracao', bonita: 'decoracao', feia: 'decoracao', acessorios: 'acessorios', incluido: 'acessorios', inclui: 'acessorios', vem: 'acessorios', consumiveis: 'consumiveis', substituicao: 'consumiveis', repor: 'consumiveis' },
    en: { noisy: 'noise', loud: 'noise', quiet: 'noise', sound: 'noise', smell: 'odour', smells: 'odour', odor: 'odour', odors: 'odour', stink: 'odour', phone: 'app', smartphone: 'app', wifi: 'app', internet: 'app', kittens: 'kitten', baby: 'kitten', price: 'cost', expensive: 'cost', money: 'cost', delivery: 'shipping', ship: 'shipping', deliver: 'shipping', arrive: 'shipping', plug: 'power', electricity: 'power', battery: 'power', mains: 'power', refund: 'return', returns: 'return', exchange: 'return', broken: 'warranty', breaks: 'warranty', guarantee: 'warranty', size: 'dimensions', big: 'dimensions', measure: 'dimensions', space: 'apartment', small: 'apartment', flat: 'apartment', dog: 'animals', dogs: 'animals', rabbit: 'animals', weigh: 'weight', heavy: 'weight', kg: 'weight', install: 'assemble', setup: 'assemble', assembly: 'assemble', installments: 'pay', klarna: 'pay', payment: 'pay', card: 'pay', paypal: 'pay', bag: 'bags', wash: 'clean', cleaning: 'clean', silica: 'litter', tofu: 'litter', clumping: 'litter', adapt: 'adapt', adjust: 'adapt', scared: 'adapt', cats: 'cat', two: 'cat', three: 'cat', included: 'accessories', include: 'accessories', box: 'accessories' },
    es: { ruidosa: 'ruido', ruidoso: 'ruido', sonido: 'ruido', silenciosa: 'ruido', silencioso: 'ruido', olor: 'olores', huele: 'olores', movil: 'aplicacion', telefono: 'aplicacion', app: 'aplicacion', wifi: 'aplicacion', gatito: 'gatitos', cachorro: 'gatitos', precio: 'cuesta', coste: 'cuesta', caro: 'cuesta', cara: 'cuesta', entrega: 'envio', envian: 'envio', llega: 'envio', enchufe: 'corriente', electricidad: 'corriente', bateria: 'corriente', pilas: 'corriente', devolver: 'devolucion', reembolso: 'devolucion', cambiar: 'devolucion', averia: 'garantia', rompe: 'garantia', tamano: 'dimensiones', medidas: 'dimensiones', mide: 'dimensiones', espacio: 'apartamento', piso: 'apartamento', pequeno: 'apartamento', perro: 'animales', perros: 'animales', conejo: 'animales', pesa: 'peso', kg: 'peso', instalar: 'montar', montaje: 'montar', plazos: 'pagar', klarna: 'pagar', pago: 'pagar', tarjeta: 'pagar', bolsa: 'bolsas', lavar: 'limpieza', limpiar: 'limpieza', silice: 'arena', tofu: 'arena', aglomerante: 'arena', adaptar: 'adapta', acostumbra: 'adapta', miedo: 'adapta', gato: 'gatos', dos: 'gatos', tres: 'gatos' },
    fr: { bruyant: 'bruit', bruyante: 'bruit', son: 'bruit', silencieuse: 'bruit', silencieux: 'bruit', odeurs: 'odeur', sent: 'odeur', telephone: 'application', app: 'application', appli: 'application', wifi: 'application', chaton: 'chatons', bebe: 'chatons', prix: 'coute', cout: 'coute', cher: 'coute', chere: 'coute', livraison: 'expedition', livre: 'expedition', arrive: 'expedition', delai: 'expedition', prise: 'courant', electricite: 'courant', batterie: 'courant', piles: 'courant', retourner: 'retour', rembourse: 'retour', remboursement: 'retour', echange: 'retour', panne: 'garantie', casse: 'garantie', taille: 'dimensions', mesure: 'dimensions', mesures: 'dimensions', grande: 'dimensions', espace: 'appartement', petit: 'appartement', petite: 'appartement', chien: 'animaux', chiens: 'animaux', lapin: 'animaux', pese: 'poids', kg: 'poids', lourd: 'poids', installer: 'monter', installation: 'monter', montage: 'monter', mensualites: 'payer', klarna: 'payer', paiement: 'payer', carte: 'payer', sac: 'sacs', laver: 'nettoyage', nettoyer: 'nettoyage', silice: 'litiere', tofu: 'litiere', agglomerante: 'litiere', adapter: 'adapte', habituer: 'adapte', peur: 'adapte', chat: 'chats', deux: 'chats', trois: 'chats' }
  };
  function askNorm(t) {
    return String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ');
  }
  function askTokens(t, lang) {
    var stop = ASK_STOP[lang] || ASK_STOP.en, sin = ASK_SIN[lang] || {};
    var stops = {}; stop.split(' ').forEach(function (w) { stops[w] = 1; });
    var out = {};
    askNorm(t).split(/\s+/).forEach(function (w) {
      if (!w || w.length < 2 || stops[w] || w === 'petmaison' || w === 'box' || w === 'pro') return;
      w = sin[w] || w;
      out[w.length > 5 ? w.slice(0, 5) : w] = 1;
    });
    return out;
  }
  function initAsk() {
    qsa('[data-pm-ask]').forEach(function (caixa) {
      var sec = caixa.closest('[data-pm-faq-sec]');
      var input = qs('.pm-ask__in', caixa), limpar = qs('[data-pm-ask-clear]', caixa);
      var out = sec && qs('[data-pm-ask-out]', sec);
      if (!sec || !input || !out) return;
      var lang = (document.documentElement.lang || 'pt').slice(0, 2).toLowerCase();
      var itens = null;
      function indexa() {
        itens = [];
        qsa('[data-pm-faq] > details', sec).forEach(function (d) {
          var sum = qs('summary', d);
          var q = sum ? sum.textContent.trim() : '';
          var corpo = [].filter.call(d.children, function (c) { return c.tagName !== 'SUMMARY'; }).map(function (c) { return c.outerHTML; }).join('');
          var a = [].filter.call(d.children, function (c) { return c.tagName !== 'SUMMARY'; }).map(function (c) { return c.textContent; }).join(' ');
          if (q) itens.push({ kind: 'faq', q: q, html: corpo, tq: askTokens(q, lang), ta: askTokens(a, lang), el: d });
        });
        qsa('.pm-det__specs > div').forEach(function (row) {
          var dt = qs('dt', row), dd = qs('dd', row);
          if (dt && dd) itens.push({ kind: 'spec', q: dt.textContent.trim(), html: '<p>' + dd.innerHTML + '</p>', tq: askTokens(dt.textContent, lang), ta: {} });
        });
      }
      var df = null;
      function peso(k) {
        /* raridade: «gatos» está em quase todas as perguntas e vale pouco;
           «devolução» está numa e decide */
        if (!df) { df = {}; itens.forEach(function (it) { var v = {}; Object.keys(it.tq).concat(Object.keys(it.ta)).forEach(function (w) { v[w] = 1; }); Object.keys(v).forEach(function (w) { df[w] = (df[w] || 0) + 1; }); }); }
        return 1 + Math.log(itens.length / (df[k] || 1));
      }
      function pontua(it, toks) {
        var s = 0, n = 0, todos = true;
        for (var k in toks) {
          n++;
          if (it.tq[k]) s += 3 * peso(k); else if (it.ta[k]) s += 1.5 * peso(k); else todos = false;
        }
        if (n && todos) s += 1;
        return s;
      }
      function esc(t) { return String(t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
      function mostra(lista, topo) {
        var t = lista[topo];
        var h = '<article class="pm-ask__r">';
        if (t.kind === 'spec') h += '<span class="pm-ask__tag">' + esc(out.getAttribute('data-t-ficha') || '') + '</span>';
        h += '<h3 class="pm-ask__q">' + esc(t.q) + '</h3><div class="pm-ask__a">' + t.html + '</div>';
        var outros = lista.filter(function (_, i) { return i !== topo; }).slice(0, 3);
        if (outros.length) {
          h += '<div class="pm-ask__rel"><span>' + esc(out.getAttribute('data-t-rel') || '') + '</span><ul>';
          outros.forEach(function (o) { h += '<li><button type="button" data-pm-ask-pick="' + lista.indexOf(o) + '">' + esc(o.q) + '</button></li>'; });
          h += '</ul></div>';
        }
        h += '</article>';
        out.innerHTML = h;
        qsa('[data-pm-ask-pick]', out).forEach(function (b) {
          b.addEventListener('click', function () { mostra(lista, parseInt(b.getAttribute('data-pm-ask-pick'), 10)); });
        });
      }
      var timer = null;
      function corre() {
        var v = input.value.trim();
        limpar.hidden = !v;
        if (v.length < 3) { out.hidden = true; out.innerHTML = ''; sec.classList.remove('is-asking'); return; }
        if (!itens) indexa();
        var toks = askTokens(v, lang);
        var res = itens.map(function (it) { return { it: it, s: pontua(it, toks) }; })
          .filter(function (r) { return r.s >= 3; })
          .sort(function (a, b) { return b.s - a.s; })
          .slice(0, 4).map(function (r) { return r.it; });
        sec.classList.add('is-asking');
        out.hidden = false;
        if (!res.length) {
          out.innerHTML = '<div class="pm-ask__none">' + esc(out.getAttribute('data-t-none') || '') +
            '<br><a href="' + esc(caixa.getAttribute('data-contacto') || '/pages/contact') + '">' + esc(out.getAttribute('data-t-falar') || '') + ' →</a></div>';
          return;
        }
        mostra(res, 0);
      }
      input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(corre, 120); });
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); clearTimeout(timer); corre(); input.blur(); } if (e.key === 'Escape') { input.value = ''; corre(); } });
      limpar.addEventListener('click', function () { input.value = ''; corre(); input.focus(); });
    });
  }

  /* ── PetMaison Partners — o link de afiliado (pm_ref) ──────────────
     Um parceiro partilha `petmaison.pt/…?pm_ref=<slug>`. O slug fica num
     cookie 30 dias (o ÚLTIMO link ganha) e, enquanto for válido, vai para
     os atributos do carrinho (`_pm_ref`, `_pm_ref_at`): o sublinhado
     esconde-os no checkout e a encomenda chega com a origem escrita.
     Só se grava com o marketing autorizado (Customer Privacy API do
     Shopify); numa loja sem banner de consentimento não há restrição.
     Regras: nada aqui lança exceções para fora, nada aqui atrasa o resto
     do pm.js, e sem `pm_ref` válido não se faz pedido nenhum. */
  var ref = {
    DIAS: 30,
    PADRAO: /^[a-z0-9-]{2,40}$/,
    cookie: function (nome) {
      try {
        var m = document.cookie.match(new RegExp('(?:^|; )' + nome + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : '';
      } catch (e) { return ''; }
    },
    grava: function (slug, iso) {
      try {
        var fim = '; Max-Age=' + (this.DIAS * 86400) + '; Path=/; SameSite=Lax; Secure';
        /* Sem codificar: o slug é [a-z0-9-] e o ISO só tem `-:.TZ`, todos
           legais num cookie — e assim quem o ler do lado do servidor vê a
           data tal e qual, sem %3A. */
        document.cookie = 'pm_ref=' + slug + fim;
        document.cookie = 'pm_ref_at=' + iso + fim;
      } catch (e) { /* cookies bloqueados */ }
    },
    /** O cookie ainda vale? Devolve {slug, at} ou null. */
    valido: function () {
      try {
        var slug = this.cookie('pm_ref'), at = this.cookie('pm_ref_at');
        if (!this.PADRAO.test(slug) || !at) return null;
        var t = Date.parse(at);
        if (isNaN(t) || Date.now() - t > this.DIAS * 86400000) return null;
        return { slug: slug, at: at };
      } catch (e) { return null; }
    },
    /** O slug do URL — só minúsculas, dígitos e hífen; o resto é ignorado. */
    doUrl: function () {
      try {
        var m = /[?&]pm_ref=([^&#]*)/.exec(window.location.search);
        if (!m) return '';
        var slug = decodeURIComponent(m[1].replace(/\+/g, ' ')).trim().toLowerCase();
        return this.PADRAO.test(slug) ? slug : '';
      } catch (e) { return ''; }
    },
    /** Guarda o slug — mas só quando o marketing está autorizado. */
    guarda: function (slug) {
      var self = this;
      var iso = new Date().toISOString();   /* a hora do clique no link, não a do consentimento */
      function permitido() {
        try {
          var cp = window.Shopify && window.Shopify.customerPrivacy;
          return !!(cp && typeof cp.marketingAllowed === 'function' && cp.marketingAllowed());
        } catch (e) { return false; }
      }
      function agora() { self.grava(slug, iso); self.sync(); }
      var S = window.Shopify;
      if (!S || typeof S.loadFeatures !== 'function') { agora(); return; }
      try {
        S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
          if (err) return;   /* a API não carregou: sem certeza, não se grava */
          if (permitido()) { agora(); return; }
          /* Ainda sem decisão (banner à vista): espera-se por ela. */
          document.addEventListener('visitorConsentCollected', function aoDecidir() {
            if (!permitido()) return;
            document.removeEventListener('visitorConsentCollected', aoDecidir);
            agora();
          });
        });
      } catch (e) { /* nunca bloquear o resto */ }
    },
    /** Passa o cookie para o carrinho, se houver artigos e ainda não lá estiver igual. */
    sync: function () {
      var r = this.valido();
      if (!r) return Promise.resolve();
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (x) { return x.ok ? x.json() : null; })
        .then(function (c) {
          if (!c || !c.item_count) return;
          var a = c.attributes || {};
          if (a._pm_ref === r.slug && a._pm_ref_at === r.at) return;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { _pm_ref: r.slug, _pm_ref_at: r.at } })
          });
        })
        .catch(function () { /* offline ou 429: fica para a próxima adição */ });
    },
    init: function () {
      try {
        var slug = this.doUrl();
        if (slug) this.guarda(slug);   /* o último link substitui o anterior */
        else this.sync();
      } catch (e) { /* nunca bloquear o resto */ }
    }
  };
  window.PMRef = ref;

  function init() {
    initAnnouncement();
    initHeaderHeight();
    menu.init();
    initNavFit();
    cart.init();
    search.init();
    initImageFade();
    initRails();
    initGallery();
    initPdp();
    initCollection();
    initCartPage();
    initLogin();
    initContent();
    initIdioma();
    initHover();
    initKlarna();
    initPrestacoes();
    initUgcPlus();
    initPress();
    initContadores();
    initDet();
    initAsk();
    initPopup();
    ref.init();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
